import { grayscaleHex } from '@redact-modules/documents/watermarkImage';
import {
  DEFAULT_FONT_SCALE,
  DEFAULT_WATERMARK_SCALE,
  WatermarkAnchorH,
  WatermarkAnchorV,
  WatermarkTextAlign,
} from '@redact-modules/documents/watermarkTextMetrics';
import { TDOId } from '@common-modules/universal/models/Brands';
import { LexiconEntrySnapshot } from '@redact-modules/lexicons/models';
import { PageRange, toPageRanges } from '@redact-modules/documents/pageRanges';
import { ExternalIntegrationConfig } from '@helpers/externalIntegrationApi';

// 'initial' = the on-open default: behaves like 'fit-width' but capped so a
// wide editor doesn't blow a small page up past native size. It exists only
// until the user touches any zoom control, at which point the mode flips to a
// real (uncapped) 'fit-width' / 'fit-page' / 'manual'.
export type ZoomMode = 'fit-page' | 'fit-width' | 'manual' | 'initial';
// 'highlight' = Remove Page tool (slip-sheet); 'exclude' = Delete Page tool (drop
// from output). Both are click-a-page toggles that mirror the page context-menu
// items. The tool ids predate the Remove/Hide rename.
// 'polygon' = free-form polygon redaction (click to add points, double-click
// to close).
// 'marquee' = drag a rectangle to multi-select every redaction it overlaps.
// 'lasso' = drag a free-form path to multi-select every redaction it encloses.
export type Tool =
  | 'select'
  | 'redact'
  | 'highlight'
  | 'exclude'
  | 'polygon'
  | 'marquee'
  | 'lasso';
export type ActiveTab = 'editor' | 'redacted-file';
export type DetectionsTab = 'detections' | 'comments';
/**
 * Which source of detections the Detections tab's `All | PII | Lexicon` strip
 * is showing (VE-30011).
 *
 * `all` is not a third source — it is "do not filter by source", and it is what
 * a document with no lexicon always shows. On the session rather than in the
 * panel because the CANVAS honours it too (`selectFilteredDetections`):
 * switching to PII used to narrow the list and leave every lexicon overlay on
 * the page.
 */
export type DetectionSource = 'all' | 'pii' | 'lexicon';

/**
 * Inner-tab in the Redactions top-level panel (VE-20839). Lives on
 * the session so DetectionMaskOverlay can gate its candidate (unchecked)
 * rendering on "is the user actually looking at PII?" — when the
 * subtab is anything else, candidate overlays hide and only checked
 * PII paints onto the canvas.
 */
export type RedactionsSubtab =
  | 'all'
  | 'pii'
  | 'drawn'
  | 'text-select'
  | 'keyword';

/**
 * Bounding box in PDF user-space coordinates (top-left origin).
 */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type PiiTypeId =
  // Direct (10)
  | 'full_name'
  | 'ssn'
  | 'dob'
  | 'phone'
  | 'email'
  | 'address'
  | 'license_plate'
  | 'credit_card'
  | 'medical_record_number'
  | 'ip_address'
  // Indirect (9)
  | 'employer'
  | 'school'
  | 'race'
  | 'gender'
  | 'age'
  | 'religion'
  | 'salary'
  | 'vehicle'
  | 'ip_prefix'
  // Catch-all for engine labels that don't map to a known category
  // (VE-21915). Keeps unrecognized PII detections visible rather than
  // dropping them; bucketed as indirect.
  | 'other';

/**
 * Human-readable label per PII type. Exported once so DetectionCard,
 * DetectionsPanel, and DetectionMaskOverlay all show the same name
 * for a given category. Older builds duplicated this table in each
 * file — keep it here so a new category lands in one place.
 */
export const PII_TYPE_LABELS: Record<PiiTypeId, string> = {
  full_name: 'Full Name',
  ssn: 'Social Security Number',
  dob: 'Date of Birth',
  phone: 'Telephone Number',
  email: 'Email Address',
  address: 'Address',
  license_plate: 'License Plate',
  credit_card: 'Credit Card',
  medical_record_number: 'Medical Record Number',
  ip_address: 'IP Address',
  employer: 'Employer',
  school: 'School / Education',
  race: 'Race or Ethnicity',
  gender: 'Gender / Gender Identity',
  age: 'Age',
  religion: 'Religion',
  salary: 'Salary / Compensation',
  vehicle: 'Vehicle',
  ip_prefix: 'IP Prefix',
  other: 'Other',
};

export interface PiiOccurrence {
  page: number;
  rects: BoundingBox[];
  /**
   * Vendor match id for this occurrence (the match on this page). Keys the
   * persisted `PersistedPiiMatch` records so added/redacted/code survive
   * reload without content+geometry overlap matching. Undefined for occurrences
   * parsed from a pre-id engine asset (those fall back to the legacy overlap
   * path). When several vendor matches collapse onto one page bucket, this is
   * the first match's id and the rest are listed in `matchIds`.
   */
  id?: string;
  /** Every vendor match id folded into this page occurrence (≥1 when set). */
  matchIds?: string[];
}

/**
 * Per-page PDF dimensions in user-space points, keyed by 1-based page number.
 * Captured from a pdfjs metadata pass (VE-21915) so normalized engine
 * coordinates can be converted into the editor's PDF-point geometry.
 */
export type PageDimensions = Record<number, { width: number; height: number }>;

/**
 * One PII occurrence as emitted by the document PII detection engine in its
 * `vtn-standard` result asset (VE-21915). `boundingPoly` points are
 * normalized 0–1 of the page, top-left origin.
 */
export interface PiiEngineResultObject {
  type?: string;
  /** Engine label, e.g. PERSON / LOCATION / ICD10_CODE. */
  label: string;
  /** Matched text. */
  text?: string;
  confidence?: number;
  /** 1-based page number. */
  page: number;
  boundingPoly: Array<{ x: number; y: number }>;
  /**
   * Vendor-assigned ids for this match. `vendor.id` is unique per individual
   * match (one text span on one page) and is the primary key for tracking
   * added/redacted state (see `PersistedPiiMatch`); `vendor.groupId` is shared
   * by every match of a common text phrase and clusters them into one PII card
   * (`PiiDetection`). Optional so older result assets (pre-id engine) still
   * parse — those fall back to synthesized ids + `piiType|text` grouping.
   */
  vendor?: {
    id?: string;
    groupId?: string;
  };
}

/**
 * Body of the document PII detection engine's `vtn-standard` result asset.
 * `sourceEngineId` matches the configured documentPIIDetectionEngineId.
 */
export interface PiiEngineResult {
  sourceEngineId?: string;
  taskId?: string;
  generatedDateUTC?: string;
  object?: PiiEngineResultObject[];
}

export interface RedactionCodeAssignment {
  id: string;
  label: string;
  color: string;
}

/**
 * Fields every detection card carries, whatever produced it.
 *
 * Split out of `PiiDetection` for VE-24948 / U5: lexicon matches land in the
 * same Detections panel (U5-FR14/FR18) and reuse the same triage machinery, but
 * they have no vendor `piiType`, `category` or `confidence`. Rather than widen
 * `PiiDetection` with dishonest values for those, the shared fields live here
 * and the two sources are discriminated variants (see `Detection`).
 */
export interface DetectionBase {
  id: string;
  label: string;
  occurrences: PiiOccurrence[];
  // user-facing state (added by the editor; not from the engine)
  checked: boolean | 'partial';
  expanded: boolean;
  checkedPages?: number[];
  /**
   * Per-match redaction set — the vendor match ids currently redacted (burned
   * in) for this detection. The source of truth for redaction state when the
   * detection's occurrences carry vendor ids (lets two matches on the same
   * page differ, e.g. redact one occurrence via the overlay menu). `checked` /
   * `checkedPages` are derived from it (see `deriveDetectionRedaction`). For
   * legacy detections without vendor ids this stays undefined and the
   * page-level `checkedPages` remains authoritative.
   */
  redactedMatchIds?: string[];
  /**
   * Vendor match ids the user has deleted from the Redactions list (the
   * per-match trash). A deleted match is hidden on the canvas and excluded
   * from redaction/checkbox math, but its detection stays listed as long as
   * other matches remain. Persisted via `PersistedPiiMatch.deleted` so the
   * deletion survives reload (a partially-deleted group keeps its records).
   */
  deletedMatchIds?: string[];
  /**
   * Of `redactedMatchIds`, the ones AUTO REDACT redacted rather than a person
   * (VE-24948). Lexicon detections only.
   *
   * Recorded so the saved asset can still answer "did anybody look at this
   * before it was burned in?". It cannot be reconstructed afterwards: a
   * reviewer opening the document later leaves no trace of what was already
   * redacted when they arrived, so a match redacted by the system and one
   * redacted by hand are otherwise identical.
   *
   * NOT cleared when a reviewer un-redacts and re-redacts the same match — the
   * flag describes how the match FIRST became redacted, which is the question
   * being asked.
   */
  autoRedactedMatchIds?: string[];
  redactionCode?: RedactionCodeAssignment;
  /**
   * Whether this detection has been promoted to the Redactions list (the
   * "ADD" action on the PII tab). Decoupled from `checked` so a PII match
   * behaves like other redaction types: unredacting clears `checked` but
   * leaves it in the Redactions list, while Delete clears `addedToRedactions`
   * and removes it entirely. Set true automatically whenever the detection
   * becomes redacted; only Delete / reset clears it. Default falsy = a
   * candidate still living on the PII tab.
   */
  addedToRedactions?: boolean;
}

/**
 * A detection produced by the PII engine.
 *
 * `source` is required so the variant is discriminated at the type level: code
 * that reads `piiType`, `category` or `confidence` must first establish it is
 * looking at a PII row, which the compiler now enforces.
 */
export interface PiiSourcedDetection extends DetectionBase {
  source: 'pii';
  /**
   * Vendor group id shared by every match of this phrase (equals the vendor
   * `groupId`; mirrored onto `id` when present so the card has a stable
   * identity across re-runs). Undefined when parsed from a pre-id engine
   * asset. See `PiiEngineResultObject.groupId`.
   */
  groupId?: string;
  piiType: PiiTypeId;
  category: 'direct' | 'indirect';
  confidence: number;
}

/**
 * A detection produced by applying a custom lexicon (VE-24948 / U5, U5-FR15).
 *
 * Carries a SNAPSHOT of the entry that produced it rather than a reference to a
 * live lexicon. That is the mechanism behind U5-FR2 — editing or deleting a
 * lexicon cannot reach back into a document, because nothing here dereferences
 * one — and it is what keeps the source indicator meaningful after the lexicon
 * is gone.
 */
export interface LexiconSourcedDetection extends DetectionBase {
  source: 'lexicon';
  lexicon: LexiconEntrySnapshot;
}

/**
 * Any detection card in the panel.
 *
 * Prefer this over `PiiDetection` for anything that reads the session's
 * detection list, since a lexicon row can appear there.
 */
export type Detection = PiiSourcedDetection | LexiconSourcedDetection;

/**
 * Historical name for a PII-engine detection.
 *
 * Retained as an alias so the many call sites that genuinely only handle PII
 * rows keep reading naturally. New code touching the session's detection list
 * should use `Detection`.
 */
export type PiiDetection = PiiSourcedDetection;

/** Narrows a detection to the lexicon variant (U5-FR15). */
export function isLexiconDetection(
  detection: Detection
): detection is LexiconSourcedDetection {
  return detection.source === 'lexicon';
}

/** Narrows a detection to the PII-engine variant. */
export function isPiiDetection(
  detection: Detection
): detection is PiiSourcedDetection {
  return detection.source === 'pii';
}

/**
 * The category line shown under a detection's label (VE-24948 / U5).
 *
 * PII rows show the humanized engine type. Lexicon rows show the LEXICON NAME
 * from the snapshot — never a live lookup (BR-44) — so the line survives the
 * lexicon being renamed or deleted (`U5-FR2` / `U5-FR15`).
 *
 * THE NAME ALONE, with no "Lexicon · " in front of it. The word was doing a
 * job the book glyph does better and shorter: every surface that renders this
 * puts the icon beside it, so spelling out the kind as well said the same
 * thing twice and spent a third of a narrow rail's width doing it. Callers
 * that need the kind in words (a tooltip, an accessible name) say so
 * themselves rather than having it baked into the label.
 */
export function detectionCategoryLabel(detection: Detection): string {
  return isLexiconDetection(detection)
    ? detection.lexicon.lexiconName
    : (PII_TYPE_LABELS[detection.piiType] ?? detection.piiType);
}

// ---------------------------------------------------------------------------
// Applied lexicons (VE-24948 / U5)
// ---------------------------------------------------------------------------

/**
 * One lexicon applied to a document, as recorded against that document.
 *
 * Written by both entry points: the upload selector (`U5-FR14`, `source:
 * 'upload'`), the editor apply panel (`U5-FR18`, `source: 'editor'`) and the
 * `autoApply` flag an admin set in settings (`source: 'auto'` — nobody pressed
 * anything, and the record must not claim otherwise). BR-51
 * requires `source` because `U5-FR16`'s audit trail distinguishes them.
 */
export interface AppliedLexiconRecord {
  lexiconId: string;
  /**
   * The lexicon's name AS AT application time.
   *
   * Frozen deliberately, like `LexiconEntrySnapshot.lexiconName`: a renamed
   * lexicon must not retroactively relabel what a reviewer already applied
   * (`U5-FR2`).
   */
  lexiconName: string;
  /** Which revision of the lexicon was applied — settings may now hold a later one. */
  revision: number;
  appliedAt: string;
  appliedBy: string;
  source: 'upload' | 'editor' | 'auto';
  /**
   * Whether this application's matches were promoted straight to redactions
   * instead of arriving as detections to review (VE-24948).
   *
   * PER DOCUMENT, not read from the library at evaluation time. The lexicon's
   * own `autoRedact` seeds the upload picker's toggle, and what the picker
   * wrote is what this document is entitled to say happened to it — the same
   * reason `lexiconName` and `revision` are frozen here (`U5-FR2`). A library
   * flag flipped afterwards must not change the account of a run that already
   * happened.
   *
   * Absent reads as OFF. It has to: every record written before this field
   * existed describes a run that put its matches up for review.
   */
  autoRedact?: boolean;
  /**
   * When matching last ran for this record, or `null` when it has not run.
   *
   * This is BR-50's "unevaluated" marker, and it exists because the upload path
   * persists a selection WITHOUT evaluating it (BR-49) — page text does not
   * exist until the editor opens the document. First open sees `null` and
   * evaluates.
   *
   * Not derived from "does this lexicon have any detections?": a lexicon can
   * legitimately match nothing, and that would then re-evaluate on every open
   * forever.
   */
  evaluatedAt?: string | null;
  /**
   * WHICH FILE that evaluation read — the `media` asset id the session held
   * when matching ran, or null/absent for a record written before this existed.
   *
   * `evaluatedAt` says a run happened; it cannot say what it happened AGAINST.
   * That distinction is the whole OCR problem: a lexicon applied to a scanned
   * document matches nothing, stamps `evaluatedAt`, and is then skipped for
   * ever — `watchFirstOpen` only takes unevaluated records, and a fresh session
   * starts with an empty `sessionAppliedLexiconIds` — so the reload onto OCR's
   * searchable copy never re-ran it.
   *
   * Comparing this against the session's current `mediaAssetId` makes STALENESS
   * DERIVABLE rather than a flag someone has to remember to clear on the reload
   * path. It generalises past OCR too: any time the document's bytes change,
   * every lexicon evaluated against the old ones is stale by construction.
   *
   * Absent reads as "unknown, assume stale" only where an asset id is available
   * to compare with — see `pendingLexiconIds`. A record from before this field
   * existed must not re-evaluate for ever on documents that never change.
   */
  evaluatedAssetId?: string | null;
  /**
   * Present when the run that produced this record did NOT finish cleanly.
   *
   * `LexiconApplyState` is session-scoped and never persisted, so "results may
   * be incomplete" and "N entries could not be evaluated" vanished on reopen
   * while `evaluatedAt` stayed set — leaving a document that asserted a lexicon
   * was applied over an under-reported or empty match set, with nothing saying
   * so (review-gate B3). Durable, because the gap outlives the session that
   * created it. Counts only, never entry terms (NFR-S7 / SEC-R3).
   *
   * Whether a mark still names a cause is `hasIncompleteCause`: add any new
   * cause there too, or a record carrying only that cause reads as clean.
   */
  incomplete?: {
    /** A budget stopped the run early (ADR-001). */
    timedOut: boolean;
    /** The matching worker died on at least one page (review-gate #3 BR-12). */
    crashed?: boolean;
    /** Entries the matcher gave up on (BR-47). */
    abandonedEntryCount: number;
    /** Matches found but not placeable (review-gate B9). */
    unresolvedGeometryCount: number;
    /**
     * @deprecated READ-ONLY, for records written before VE-29686. New writes
     * go to `unsearchedPageRanges`, and `appliedLexiconsFromAssetBody`
     * migrates this key forward on read, so it drains out of a document the
     * first time it is opened and autosaved.
     *
     * REMOVE AFTER 2027-04-01, not "once no asset can carry it". That
     * condition is unobservable: the drain needs each document opened AND
     * saved, and there is no backfill job, counter or telemetry that could
     * tell anyone it had completed. A dated sunset is a decision somebody can
     * actually execute. Removing it early costs the page list on documents
     * nobody has reopened since; removing it never is the real risk.
     *
     * Page numbers that went unsearched (review-gate #3 BR-11). Page NUMBERS
     * only — never text, never terms (NFR-S7 / SEC-R3).
     */
    unsearchedPages?: number[];
    /**
     * Page numbers that went unsearched, as inclusive `[first, last]` spans
     * (review-gate #3 BR-11). Supersedes `unsearchedPages` (VE-29686).
     *
     * RANGES BECAUSE THIS IS PERSISTED AND THE LIST IS DOCUMENT-LENGTH. Once
     * the unindexed tail is actually reported — the bug VE-29686 fixes — an
     * index that dies early on a 1,602-page document leaves ~1,600 page
     * numbers here, per applied lexicon, re-uploaded on every autosave and
     * kept for five asset generations. See `pageRanges.ts` for why that case
     * compresses and the scattered one does not matter.
     *
     * Still page NUMBERS only (NFR-S7 / SEC-R3). Read it through
     * `unsearchedRangesOf`, which falls back to the legacy field and rejects
     * a shape the asset should not have contained.
     */
    unsearchedPageRanges?: PageRange[];
    /**
     * The index was partial and the document's page count was not yet known,
     * so `unsearchedPageRanges` could not name the pages it missed
     * (VE-29686). Recorded so an empty list never stands in for "don't know".
     */
    pageCountUnknown?: boolean;
    /**
     * This run read a FAILED text index (VE-29686).
     *
     * NOT a cause on its own — see `hasIncompleteCause`, which deliberately
     * ignores it. A failed index whose pages the render path all backfilled
     * left nothing unsearched, and a mark for it would be a chip naming no
     * gap. What this field changes is the REMEDY: when there IS a gap, a
     * failed index is the one cause "Apply again" cannot fix, because
     * re-applying reads the same dead index. Only reopening re-runs the
     * pre-pass.
     *
     * `pageCountUnknown` used to stand in for this and only covers the
     * narrower case where the count was never learned. The common shape — an
     * index that dies PART-WAY, leaving a known count and a listed tail — had
     * no discriminator at all, so the row told the reviewer to apply again.
     */
    indexFailed?: boolean;
    /**
     * Evaluated before the text index finished; re-run on the next open (an
     * exception to BR-44 — see the `sagas/lexiconApply.ts` header).
     */
    indexing?: boolean;
  };
}

/**
 * The pages an `incomplete` mark says went unsearched, as ranges.
 *
 * THE ONE READER of both fields. New marks carry `unsearchedPageRanges`;
 * marks written before VE-29686 carry the flat `unsearchedPages`, and dropping
 * them would turn a recorded gap into a clean-looking record on the next
 * autosave. Returns `[]` when neither is set — which is NOT "no pages missed"
 * if `pageCountUnknown` is true (see `hasIncompleteCause`).
 *
 * AND IT VALIDATES, because this value comes off a persisted asset that is NOT
 * validated below the top three scalars (`appliedLexiconsFromAssetBody`). A
 * flat `number[]` is what every pre-VE-29686 asset holds and what a truncated
 * or foreign write would most likely produce, and destructuring a number
 * throws — in RENDER, which unmounts the applied rows and takes the
 * "incomplete" chip with them. Losing the only durable notice that pages went
 * unsearched is the worst failure shape in this product, so a malformed span
 * is dropped and the rest of the list still renders.
 *
 * DROPPING IS NOT FORGETTING, which is what `dropped` is for. A validator that
 * silently narrows a disclosure trades a loud failure for a quiet one, and the
 * quiet one is durable: a mark whose ONLY cause was an unreadable list would
 * otherwise come back `[]`, read as causeless, and autosave clean over pages
 * nobody searched. Callers that persist the result must turn `dropped` into
 * `pageCountUnknown` — "pages were missed and cannot be listed" is exactly the
 * field that already means this.
 */
export interface UnsearchedCoverage {
  ranges: PageRange[];
  /** The stored value carried spans this could not read. Fail closed. */
  dropped: boolean;
}

export function unsearchedCoverageOf(
  mark: NonNullable<AppliedLexiconRecord['incomplete']>
): UnsearchedCoverage {
  const stored = mark.unsearchedPageRanges;
  if (stored !== undefined) {
    if (!Array.isArray(stored)) {
      // Not even a list. Nothing readable, and something was meant to be here.
      return { ranges: [], dropped: true };
    }
    const ranges = stored.filter(
      (span): span is PageRange =>
        Array.isArray(span) &&
        span.length === 2 &&
        Number.isInteger(span[0]) &&
        Number.isInteger(span[1]) &&
        // Pages are 1-based. A zero or negative bound is corrupt, not a page.
        span[0] >= 1 &&
        // Reversed spans are not "empty", they are corrupt: silently reading
        // one as zero pages would erase a recorded gap.
        span[0] <= span[1]
    );
    return { ranges, dropped: ranges.length !== stored.length };
  }
  // The deliberate legacy read this function exists to contain. `toPageRanges`
  // sorts and de-duplicates, so a malformed flat list cannot produce a
  // reversed span.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const legacy = mark.unsearchedPages;
  if (legacy === undefined) {
    return { ranges: [], dropped: false };
  }
  if (!Array.isArray(legacy)) {
    return { ranges: [], dropped: true };
  }
  const pages = legacy.filter((p) => Number.isInteger(p) && p >= 1);
  return {
    ranges: toPageRanges(pages),
    dropped: pages.length !== legacy.length,
  };
}

/** `unsearchedCoverageOf(mark).ranges`, for readers that only display them. */
export function unsearchedRangesOf(
  mark: NonNullable<AppliedLexiconRecord['incomplete']>
): PageRange[] {
  return unsearchedCoverageOf(mark).ranges;
}

/**
 * Does an `incomplete` mark name anything a reviewer can act on, other than
 * `indexing` (which only schedules a re-run)? The single list of causes, so a
 * mark is never dropped as empty while it still carries one (VE-29686).
 */
export function hasIncompleteCause(
  mark: NonNullable<AppliedLexiconRecord['incomplete']>
): boolean {
  return (
    mark.timedOut ||
    mark.crashed === true ||
    mark.abandonedEntryCount > 0 ||
    mark.unresolvedGeometryCount > 0 ||
    // Through `unsearchedRangesOf`, so a legacy record's pages still count as
    // a cause and the mark is not dropped as vacuous.
    unsearchedRangesOf(mark).length > 0 ||
    mark.pageCountUnknown === true
  );
}

/**
 * The same question asked of a RUN's outcome rather than a record's mark: did
 * this apply leave anything unfinished? The audit trail reads it, so a cause
 * `hasIncompleteCause` knows about cannot be missing from the disclosure CSV.
 * Keep the two in step (VE-29686).
 */
export function runLeftWorkUnfinished(run: {
  readonly abandonedEntryIds: readonly string[];
  readonly timedOut: boolean;
  readonly crashed?: boolean;
  readonly unsearchedPages?: readonly number[];
  readonly pageCountUnknown?: boolean;
  readonly disposition?: { readonly unresolvedGeometry: number };
}): boolean {
  return (
    run.abandonedEntryIds.length > 0 ||
    run.timedOut ||
    run.crashed === true ||
    (run.unsearchedPages?.length ?? 0) > 0 ||
    run.pageCountUnknown === true ||
    (run.disposition?.unresolvedGeometry ?? 0) > 0
  );
}

/**
 * Why a lexicon apply failed outright, as a code the panel maps to a sentence
 * and a REMEDY (VE-29686).
 *
 * The remedies genuinely differ, which is why these are not one error:
 *   - `noExtractableText` — a fact about the DOCUMENT. Run OCR.
 *   - `indexFailed`       — a fact about this SESSION's index. Reopen; the
 *                           pre-pass runs again. Reported as `noExtractableText`
 *                           it sent reviewers to OCR a document whose text
 *                           layer was fine.
 *   - `lexiconUnreachable`— a fetch failed. Retrying genuinely helps.
 */
export type LexiconApplyErrorCode =
  | 'noExtractableText'
  | 'indexFailed'
  | 'lexiconUnreachable';

/** Status of the current/last lexicon application in the open session. */
export type LexiconApplyStatus =
  | 'idle'
  /** Selection recorded, but no page text has been extracted yet (BR-48). */
  | 'awaiting-text'
  | 'running'
  | 'done'
  | 'error';

/**
 * Progress and outcome of lexicon application in the open session.
 *
 * Session-scoped and never persisted: it describes one run, not the document.
 */
export interface LexiconApplyState {
  status: LexiconApplyStatus;
  /** Correlates progress/outcome with the run that produced it; stale runs are dropped. */
  requestId: string | null;
  /** Pages reported by the matcher so far, for the panel's progress line. */
  pagesMatched: number[];
  /** Matches merged in by the newest completed run. */
  matchCount: number;
  /**
   * Entries the matcher gave up on — a regex throw or an exhausted budget.
   * BR-47 requires these be REPORTED to the user, never silently dropped.
   *
   * Held as ids because they are what the matcher produces and what the count
   * is taken from. They are NOT what the panel prints: see
   * `abandonedLexiconNames`.
   */
  abandonedEntryIds: string[];
  /**
   * The lexicons those abandoned entries came from, by name — what the panel
   * actually shows beside the count.
   *
   * Terms stay out of this by NFR-S7 / SEC-R3, and ids were shown for a while
   * instead, which named nothing a reviewer could look up. Can be empty with a
   * non-zero `abandonedEntryIds`, for an entry that resolved to no lexicon at
   * all. Optional so a state written before this field existed still loads.
   */
  abandonedLexiconNames?: string[];
  /**
   * Where the matches went when `matchCount` is lower than what was found.
   *
   * Without it a 0 is ambiguous: nothing matched, everything was already
   * applied, and everything was adopted by M2 all render the same, and only the
   * first of those is worth alarming a reviewer about.
   */
  disposition?: {
    alreadyPresent: number;
    folded: number;
    dismissed: number;
    unattributed: number;
    /**
     * Applied lexicons that contain no entries at all (BR-4).
     *
     * Its own number because "the lexicon you applied is empty" is a different
     * answer from "nothing in it matched", and only the first is fixable by the
     * person reading the panel. Reported as a COUNT, never as names or terms —
     * the panel already lists the names it offered (NFR-S7 / SEC-R3).
     */
    emptyLexicons: number;
    /**
     * Matches the matcher found but no geometry could be produced for.
     *
     * Its own number because it is the one zero-shaped outcome that is a
     * DEFECT rather than a fact about the document: the text matched, and the
     * card is missing because page geometry was unavailable. Reported so it
     * can never again present as a plain "No matches found." (review-gate B9).
     */
    unresolvedGeometry: number;
  };
  /** True when a budget stopped the run early (ADR-001). */
  timedOut: boolean;
  /**
   * True when the matching worker DIED on at least one page.
   *
   * Distinct from `timedOut` because the two need different sentences and
   * different remedies: a budget stop is slowness and "Apply again" may help,
   * while a dead worker is usually a deployment fault (the worker chunk failing
   * to load is a documented prod-only breakage point here) and re-applying will
   * fail the same way (review-gate #3 BR-12).
   */
  crashed: boolean;
  /**
   * Pages that went unsearched — deadline elapsed or worker died.
   *
   * The reviewer's actionable fact: knowing page 7 was skipped is what decides
   * between re-applying and re-opening the document (review-gate #3 BR-11).
   */
  unsearchedPages: number[];
  /** The last run read its pages before the text index finished. */
  indexing?: boolean;
  /**
   * The last run read a FAILED index whose page count was never learned, so
   * `unsearchedPages` cannot list what it missed (VE-29686).
   */
  pageCountUnknown?: boolean;
  /**
   * The last run read a FAILED index that DID leave a gap (VE-29686).
   *
   * Carried on the run, not only on the record, because the banner and the
   * audit trail are the other two surfaces that have to name the remedy. With
   * it on the record alone, the part-way failure — the common shape — showed
   * the reviewer "Pages 3–10 were not searched." with no remedy at all, and
   * wrote an audit line naming the pages with no cause, while the RARER
   * unknown-count case got an explicit one. Same mark as
   * `incomplete.indexFailed`: set only when there is a gap to have a remedy
   * for.
   */
  indexFailed?: boolean;
  /**
   * Why the apply failed: a `LexiconApplyErrorCode` the panel maps to its own
   * sentence and remedy, or an untrusted diagnostic no surface may render.
   *
   * Stays `string` on purpose — see `LEXICON_APPLY_FAILURE`. The unrecognised
   * case is not a gap to close by narrowing the type; it is the behaviour that
   * keeps regex-engine text off the screen (NFR-S7 / SEC-R3).
   */
  errorMessage?: string;
  /**
   * Lexicons applied during THIS session, so page text arriving later can be
   * re-evaluated against them (BR-48).
   *
   * Session-scoped on purpose. Re-evaluating across sessions would have to
   * re-read the live lexicon, and that is precisely how an edited lexicon would
   * reach back into an already-applied document (`U5-FR2`). Within one session
   * the entry set is the one the user just applied, so extending it to pages
   * that arrive moments later is the same application, not a new one.
   */
  sessionAppliedLexiconIds: string[];
  /**
   * The AUTO REDACT half of this run, when there is one (VE-24948).
   *
   * Its own object because it drives a BLOCKING dialog, and the three things
   * that dialog needs to know are not derivable from `status`: whether this
   * run redacts at all, whether the reviewer has called it off, and what it
   * did when it finished.
   */
  autoRedact: {
    /**
     * This run will redact what it finds. Drives the blocking progress dialog,
     * which is shown for THIS case alone — an ordinary apply streams into the
     * panel and must not take the document away from the reviewer.
     */
    running: boolean;
    /**
     * The reviewer pressed Cancel, which means SKIP THE REDACTION — not stop
     * the search. The matching still finishes and its matches still arrive as
     * detections to review; only the promotion is abandoned. Stopping the
     * search would throw away work that has to be redone on the next open, to
     * decline something the reviewer can equally decline afterwards.
     */
    cancelled: boolean;
    /** Matches the completed run redacted automatically. */
    redactedCount: number;
    /**
     * WHICH matches those are — the exact set "Remove Auto Redaction" undoes.
     *
     * Recorded beside the count because the dialog offers a number and a
     * button, and the two have to denote the same matches. They did not: the
     * button walked every card carrying `autoRedactedMatchIds`, which is
     * DURABLE and survives a reopen, so a dialog reporting 3 could take back
     * 43 — including matches a reviewer had accepted in an earlier session.
     *
     * Accumulates with `redactedCount` while a summary is waiting to be
     * acknowledged, so a reviewer who is told about two runs at once can undo
     * both of them.
     */
    redactedMatchIds: string[];
    /**
     * The summary has been acknowledged, or there was nothing to say.
     *
     * Kept so the dialog cannot reappear when the panel re-renders, and so a
     * run that redacted NOTHING never shows one at all — a dialog reporting
     * that nothing happened is a click for nothing.
     */
    acknowledged: boolean;
  };
  /**
   * `sessionAppliedLexiconIds` as it was BEFORE the current request joined it
   * (captured by LEXICON_APPLY_STARTED). This is what the success snapshot
   * records so Ctrl+Z of an apply also removes its ids from the BR-48
   * re-evaluation set — snapshotting the live set would put the just-applied
   * ids INTO the undo state, and the next page-text arrival would re-apply
   * what the undo removed (review-gate finding 4).
   */
  preRequestSessionAppliedLexiconIds?: string[];
}

// `origin` is the single source of truth for a redaction's provenance —
// there is no separate `selectionMode` flag. The glyph-anchored origins are:
//   - 'keyword'     → created by keyword search (single Redact or Redact all),
//                     grouped per-phrase in the panel ("Keyword").
//   - 'text-select' → created by highlighting text (Redact Selection),
//                     grouped per-phrase-per-page ("Text").
// Both are anchored to the glyphs beneath them, so they share the
// "no drag, no resize, no stick-to-all-pages" behavior (see
// `isGlyphAnchoredRedaction`). 'manual' is the single drawn-region origin
// (rectangle by default, or a free-form polygon when `isCustomPolygon`);
// 'page-redact' is a page-level flag, not a real box.
export type RedactionOrigin =
  | 'manual'
  | 'keyword'
  | 'page-redact'
  | 'text-select';

/** A vertex of a free-form polygon redaction, in PDF user-space (top-left). */
export interface PolygonPoint {
  x: number;
  y: number;
}

export interface Redaction {
  id: string;
  origin: RedactionOrigin;
  page: number;
  geometry: BoundingBox;
  /**
   * Free-form polygon vertices (VE-21912). Set only when `isCustomPolygon` is
   * true; the vertices drive the rendered/burned-in shape, and `geometry` is
   * kept in sync as their axis-aligned bounding box so the cheap consumers
   * (thumbnail overlays, hit-testing, marquee, move deltas, list sort) keep
   * reading `geometry` unchanged. Burn-in decomposes the polygon into
   * axis-aligned MuPDF redaction rectangles (scanline strips) since MuPDF
   * ignores QuadPoints for `applyRedactions`.
   */
  points?: PolygonPoint[];
  /**
   * VE-21912: marks a `manual` redaction as a free-form polygon rather than a
   * plain rectangle. When true, `points` holds the editable vertices and the
   * editor shows per-point drag handles + renders it via PolygonLayer; when
   * false/undefined the redaction is an axis-aligned rectangle (corner-resize
   * handles). A rectangle can be converted to a polygon by setting this true
   * and seeding `points` from the four corners of `geometry`.
   */
  isCustomPolygon?: boolean;
  redactionCode?: RedactionCodeAssignment;
  /**
   * Source phrase the redaction was created from (VE-20838). Set by the
   * text-selection "Redact Selection" / "Redact All" flows so the
   * redaction's right-click menu can offer "Unredact All (N)" — N
   * counts every redaction that shares this phrase. Optional; absent
   * for redactions drawn freehand via the Draw Redaction tool.
   * In-memory only today; persistence is a follow-up.
   */
  phrase?: string;
  /**
   * When true, the redaction is drawn at the same geometry on EVERY
   * page (VE-20838). The `page` field keeps the original anchor page
   * for serialization, but rendering and burn-in expand the entry to
   * each page. Persisted with the asset body under the literal page
   * key `"all"`.
   */
  appliesToAllPages?: boolean;
  /**
   * Whether the redaction is currently redacted (burned in). Renamed from
   * `active` (VE-20839) to match the PII nomenclature. Un-redacted records are
   * skipped by the editor's render passes (rectangle overlay, polygon overlay,
   * thumbnail-rail overlay) and excluded from the burn-in plan, so they stay
   * in state for the user to re-enable but don't apply visually or in the
   * exported PDF.
   *
   * The default `undefined` is treated as redacted so existing data keeps its
   * current behavior — a redaction is un-redacted only when explicitly set
   * `false` (the only value persisted).
   */
  redacted?: boolean;
  /**
   * Display name for the redaction (VE-20839). Today used by Region
   * (`origin: 'manual'`, rectangle or polygon) cards in the right panel: the
   * card title is editable, with a default `Overlay N` assigned at
   * creation so newly-drawn boxes always have a label. Other origins
   * leave this undefined and fall back to phrase / generic preview.
   */
  name?: string;
  /**
   * Groups the per-row boxes covering ONE stretch of text, so clicking any row
   * selects the whole thing and metrics / audit labels count it once.
   *  - `text-select` — one id per selection gesture; an extending selection
   *    re-stamps the merged-into boxes (see CREATE_OR_MERGE_TEXT_REDACTIONS).
   *  - `keyword` — one id per matched occurrence, only when it wraps a line and
   *    so needs several boxes (VE-26267). Without it, deleting one row of a
   *    wrapped match leaves the rest readable.
   * Absent on single-box redactions, which are their own group.
   */
  textSelectionId?: string;
  /**
   * Number of words redacted by this row, derived from `phrase` at save time.
   * Only meaningful on `origin: 'text-select'` rows — per-row text can't be
   * re-derived after the fact (the phrase may be re-stamped on merge), so we
   * persist it (asset format 1.0.0) to make word-count metrics cheap to total.
   */
  wordCount?: number;
}

/**
 * Compound key for `detectionRectOverrides`: detectionId|page|rectIdx
 */
export type DetectionRectKey = string;

export interface KeywordMatch {
  id: string;
  page: number;
  rect: BoundingBox;
  matchedText: string;
  redacted: boolean;
}

export interface SaveState {
  status: 'clean' | 'dirty' | 'saving' | 'error';
  lastSavedAt: string | null;
  /**
   * VE-26422: the redacted version the newest save was sent to produce. `null`
   * means it was an ordinary save that was never sent for burn-in — which is
   * evidence the output is behind. `undefined` means the save predates labelling
   * and says nothing either way.
   */
  lastSavedBurnInVersion?: number | null;
  lastError?: { code: string; message: string; failedAt: string };
  /** Monotonic count of dirtying edits, used to detect edits that land mid-save. */
  rev: number;
}

/**
 * VE-25718: whether the session still holds edits the server does not have.
 *
 * `error` counts as unsaved work, not as a terminal state. A failed save leaves
 * the edits in memory and nowhere else, so every route back to a save — the
 * auto-save tick, both Save buttons, Cmd/Ctrl+S — has to stay open, or the only
 * escape from a failed save is to make a further edit (`markDirty` flips
 * `error` back to `dirty`) and the user is otherwise left holding unsaved work
 * behind a disabled button.
 *
 * `saving` is deliberately excluded: a save is already in flight for it, and
 * SAVE_SUCCESS re-flags the session `dirty` if an edit landed mid-save.
 */
export const hasUnsavedWork = (save: SaveState): boolean =>
  save.status === 'dirty' || save.status === 'error';

/**
 * Is this session worth KEEPING when the reviewer moves off it?
 *
 * Deliberately not `hasUnsavedWork`, which was reused here and should not have
 * been. That predicate answers "should we warn the reviewer before they lose
 * this", and it excludes `saving` on purpose — an autosave tick landing as
 * someone closes the editor must not accuse them of unsaved work.
 *
 * Suspending asks a different question, and `saving` is the answer that
 * differs. A save in flight has NOT landed: the session still holds the only
 * copy of those edits until the round trip returns, and it may come back
 * `error`. Dropping it on a switch destroyed the edits AND swallowed the
 * failure, because the SAVE_FAILURE that followed found no session to land on.
 */
export const isSessionWorthKeeping = (save: SaveState): boolean =>
  hasUnsavedWork(save) || save.status === 'saving';

export interface UndoSnapshot {
  /**
   * VE-28716: the selection, snapshotted with the rows it points at so undo
   * hands a bulk action's selection back rather than just its effect — Ctrl+Z
   * after "Redact 3 selections" puts the three rows back AND re-selects them,
   * ready to act on again.
   *
   * Replaces whatever is selected at the time rather than merging: a merge
   * would produce a selection the user never made. A live selection is not
   * lost by that, since the step undo pushes onto the redo stack carries it —
   * redo hands it straight back.
   */
  selectedRedactionIds: string[];
  selectedDetectionRowKeys: string[];
  redactions: Redaction[];
  detections: Detection[];
  detectionRectOverrides: Record<DetectionRectKey, BoundingBox>;
  pageRedacts: number[];
  /** VE-20838: snapshotted so Cmd/Ctrl+Z reverses page-exclude toggles. */
  excludedPages: number[];
  /**
   * VE-24948: snapshotted TOGETHER with `detections`, because the two describe
   * one fact from two sides — "this lexicon is applied" and "here is what it
   * found". Undoing an apply without this left the document asserting a lexicon
   * was applied with `evaluatedAt` set and zero findings, which autosave then
   * persisted and nothing would re-evaluate: the advertised escape hatch
   * manufactured the exact data-loss state `mergePiiResultsIntoDetections`
   * exists to prevent (review finding 6).
   */
  appliedLexicons: AppliedLexiconRecord[];
  /**
   * VE-24948: the BR-48 re-evaluation set, snapshotted with the rest so undo
   * actually ends an apply. Without it, Ctrl+Z restored `detections` and
   * `appliedLexicons` while the lexicon's id stayed in
   * `lexiconApply.sessionAppliedLexiconIds` — and the next page-text arrival
   * re-applied everything the undo had just removed (review-gate finding 4).
   * The apply's own success snapshot captures the PRE-request set (see
   * `LEXICON_APPLY_SUCCESS` in the reducer); every other snapshot captures the
   * live one.
   */
  sessionAppliedLexiconIds: string[];
  /**
   * VE-24948: set ONLY on the snapshot an apply itself pushes, to the id of
   * that apply, so undoing it can retire the run's summary.
   *
   * Two earlier versions were wrong for the same reason — both tried to infer
   * "does this snapshot predate the run" from state that answers a different
   * question. Comparing match COUNTS broke when a dismissal changed the count
   * for unrelated reasons. Recording the SETTLED request id broke because
   * `LEXICON_APPLY_STARTED` advances the id on every page-text re-evaluation
   * while `LEXICON_APPLY_SUCCESS` only snapshots when something changed: a
   * snapshot taken after apply R1, then a no-op re-evaluation R2, then Ctrl+Z
   * on an unrelated edit compared R1 to R2 and wiped the summary — including
   * `abandonedEntryIds`, the only surface where BR-47 names an entry.
   *
   * Undefined on every other snapshot, which therefore can never clear
   * anything. That is the property that makes this one correct: it is not a
   * heuristic about the run, it is a mark the run leaves on its own undo step.
   */
  lexiconApplyRequestId?: string;
  /**
   * VE-24693. Watermark actions always pushed a snapshot, but the snapshot did
   * not carry watermarks — so the cost was paid and undo restored the five
   * redaction fields around them, leaving the watermark change in place. The
   * actions are documented as undoable; now they are.
   */
  watermarks: AppliedWatermark[];
  /** Travels with `watermarks`, so undoing the switch brings the marks back. */
  watermarksOff: boolean;
  /**
   * VE-24447 — snapshotted for the same reason watermarks are: applying or
   * clearing a Bates stamp is an undoable document edit, and a snapshot that
   * omitted it would restore everything around it and leave the stamp behind.
   */
  batesStamp: BatesStampSettings | null;
  /** Travels with `batesStamp`, so undoing the switch brings the numbers back. */
  batesStampOff: BatesStampSettings | null;
}

export interface UndoState {
  past: UndoSnapshot[];
  future: UndoSnapshot[];
}

export interface RedactedVersion {
  versionId: string;
  /**
   * Monotonic version number from the asset's `details.version`. Distinct
   * from `versionId` (which is the asset id). Null only when the asset's
   * details couldn't be parsed.
   */
  version: number | null;
  redactedAt: string;
  /**
   * Signed URL to the redacted PDF. May be null briefly between burn-in
   * completion and the saga's follow-up signedUri fetch — consumers MUST
   * guard against null (skip the react-pdf <Document>, disable download
   * buttons, etc.) rather than coerce to empty string.
   */
  redactedAssetUrl: string | null;
  /**
   * User-visible filename of the redacted asset (e.g. "court case 123[REDACTED].pdf").
   * Comes from the asset's `name` field — distinct from the asset's signed
   * URL path leaf, which is just a hash.
   */
  name: string | null;
  /** Byte size of the redacted PDF. Null when not yet known on a reload. */
  assetSize: number | null;
  /** Page count of the redacted PDF. */
  numPages: number | null;
  /**
   * Count of pages the user marked "Delete Page" — dropped from the burned-in
   * output entirely. Surfaced in the Redacted Files tab as a "Pages Deleted"
   * row when > 0 (VE-20838). The field name predates the Remove/Hide rename
   * and is kept stable so existing assets keep reporting correctly.
   */
  pagesRemoved: number;
  /**
   * Count of pages the user marked "Remove Page" — kept in the output but
   * replaced with the FOIA slip-sheet. Surfaced in the Redacted Files tab's
   * metrics row alongside the drawn/text-select counts. Field name predates
   * the rename.
   */
  pagesFullyRedacted: number;
  auditLogUrl: string;
  /** Manual-origin redaction count (mirrors metrics.manual.count). */
  redactionCount: number;
  /**
   * Per-category redaction metrics (mirror `details.metrics.*`). Kept as the
   * full breakdown — not just the words-redacted sum — so the download-count
   * rewrite (which replaces `details` wholesale from memory) preserves them.
   * All zero for assets burned in before per-category metrics landed.
   */
  textSelect: TextSelectMetrics;
  keywords: KeywordMetrics;
  pii: PiiMetrics;
  /** Cumulative download count from details.metrics.downloads. */
  downloads: number;
  pushedTo: VendorPushRecord[];
}

// ---------------------------------------------------------------------------
// Redaction metrics (VE-23xxx)
//
// Per-category counts persisted on the burned-in `redacted-document` asset's
// `details.metrics` and surfaced on the Redacted File tab. Computed from the
// live session at burn-in (redacted records only). See `computeRedactionMetrics`.
// ---------------------------------------------------------------------------

/** Text-selection redaction metrics. */
export interface TextSelectMetrics {
  /** Number of text-select redaction rows. */
  rowCount: number;
  /** Total words redacted via text selection. */
  wordCount: number;
  /** Number of unique contiguous spans (distinct `textSelectionId`). */
  uniqueCount: number;
}

/** Keyword-match redaction metrics. */
export interface KeywordMetrics {
  /** Number of distinct keyword phrases. */
  uniqueCount: number;
  /** Number of keyword matches (redactions). */
  totalCount: number;
  /**
   * Total words redacted via keyword match. ≥ `totalCount` when multi-word
   * phrases were matched.
   */
  wordCount: number;
}

/** PII-match redaction metrics. */
export interface PiiMetrics {
  /** Number of distinct PII phrases redacted. */
  uniqueCount: number;
  /** Number of PII matches redacted. */
  totalCount: number;
  /** Total words redacted via PII match. */
  wordCount: number;
}

/**
 * Full per-category breakdown returned by `computeRedactionMetrics`. The
 * `PII` key matches the asset's `details.metrics.PII` casing so wiring is a
 * straight copy.
 */
export interface RedactionMetricsBreakdown {
  textSelect: TextSelectMetrics;
  keywords: KeywordMetrics;
  PII: PiiMetrics;
  /** Sum of the three categories' `wordCount`s. */
  totalWordsRedacted: number;
}

/**
 * Burn-in lifecycle state. Drives the "Redact File" button spinner and the
 * upcoming progress UI. `phase` is the user-visible step:
 *   - `idle`: no burn-in in flight
 *   - `preparing`: source PDF being fetched
 *   - `burning`: mupdf is applying redactions in the worker
 *   - `uploading`: redacted PDF being uploaded as a redacted-document asset
 *   - `error`: the last burn-in failed; `lastError` holds the message
 *
 * `error` does NOT block further attempts — clicking Redact File again
 * clears it and starts a new run.
 */
export type ExportPhase =
  | 'idle'
  | 'preparing'
  | 'burning'
  | 'uploading'
  | 'error';

export interface ExportState {
  phase: ExportPhase;
  lastError?: string;
}

export type VendorId =
  | 'govqa'
  | 'evidence-com'
  | 'casepoint'
  | 'nuix'
  | 'exterro'
  | 'foiaxpress';

export interface VendorPushRecord {
  vendor: VendorId;
  pushedAt: string;
  outcome: 'success' | 'failure';
  errorMessage?: string;
}

// ---------------------------------------------------------------------------
// "Send to" external integrations (VE-23442)
//
// Ports the mediaDetails redacted-files "Send to" buttons to the document
// editor. Five vendor integrations (each enabled by a request-id stamped on
// the TDO's `details` at ingestion) plus a generic, org-configurable
// "external integration" (the `redact-export` integration config). All reuse
// the shared send helpers under `src/helpers/*API.ts`.
// ---------------------------------------------------------------------------

/**
 * Identifies one integration slice in `DocumentsStore.integrations`. Distinct
 * from `VendorId` (the persisted push-record vendor): this list includes the
 * generic `externalIntegration` and uses the slice-key casing the document
 * store uses, whereas `VendorId` mirrors the historical media push records.
 */
export type IntegrationKey =
  | 'govQa'
  | 'foiaXpress'
  | 'casepoint'
  | 'exterro'
  | 'nuix'
  | 'externalIntegration';

/**
 * Request-ids read from the TDO's `details` at open. Each non-null id enables
 * the matching vendor "Send to" button on the Redacted File tab — mirrors the
 * `tdo.details.<vendor>RequestId` gating in MediaDetailsPage/RedactedFiles.
 */
export interface VendorRequestIds {
  govQa: string | null;
  foiaXpress: string | null;
  casepoint: string | null;
  exterro: string | null;
  nuix: string | null;
}

/**
 * Optional username/password supplied from the GovQA auth dialog. Mirrors
 * mediaDetails' `BasicIntegrationAuth`. Only GovQA prompts for credentials;
 * the other integrations read everything from the org's integration config.
 */
export interface IntegrationAuth {
  username?: string;
  password?: string;
}

/**
 * Per-integration UI/send state. Mirrors mediaDetails' `BasicIntegration`:
 * `isSendingFile` drives the button spinner, `isFormShown` the GovQA auth
 * dialog, `isStatusDialogShown` the shared success/error dialog.
 */
export interface IntegrationState {
  isSendingFile: boolean;
  isFormShown: boolean;
  isStatusDialogShown: boolean;
  authErrorMessage?: string;
  errorMessage?: string;
  successMessage?: string;
  /**
   * GovQA caches a `sessionId` from a successful auth so subsequent sends skip
   * the credential dialog; cleared (→ dialog reshown) when a cached send fails.
   * Only the `govQa` slice uses it.
   */
  sessionId?: string | null;
  /**
   * The org's `redact-export` integration config, fetched on mount so the
   * generic button can show its custom `label`. Only the `externalIntegration`
   * slice uses it.
   */
  config?: ExternalIntegrationConfig;
}

export type DocumentIntegrationsState = Record<
  IntegrationKey,
  IntegrationState
>;

/**
 * Auto-save payload shape. Mirrors the SDO under `documentRedactionStateSchemaId`.
 * Round-trip property: `JSON.parse(JSON.stringify(payload)) === payload` (structural).
 */
export interface PersistedRedactionState {
  schemaVersion: 1;
  redactions: Redaction[];
  detectionRectOverrides: Record<DetectionRectKey, BoundingBox>;
  pageRedacts: number[];
  /** Pages marked to drop from the burned-in output (VE-20838). */
  excludedPages: number[];
  detectionState: Array<{
    id: string;
    checked: boolean | 'partial';
    checkedPages?: number[];
    redactionCode?: RedactionCodeAssignment;
  }>;
  /**
   * Per-match PII records (the PII-selection mechanism), flattened across
   * pages. On the asset these live under each page bucket's `PII` array; this
   * is the in-saga flattened view. Optional so older saves round-trip.
   */
  piiMatches?: PersistedPiiMatch[];

  // -------------------------------------------------------------------------
  // Custom lexicons (VE-24948 / U5)
  //
  // Both fields are OPTIONAL and `schemaVersion` is deliberately NOT bumped.
  // The 1 -> 2 bump belongs to U1 (OCR provenance), and U5 is Wave 1 while U1
  // is Wave 2 — neither may depend on the other landing first. The existing
  // optional `piiMatches` above is the precedent for adding this way.
  // -------------------------------------------------------------------------

  /**
   * Lexicons selected for this document — at upload (U5-FR14) or applied from
   * the editor (U5-FR18).
   *
   * At upload the selection is persisted and NOT evaluated: matching needs
   * extracted page text, which only exists once the editor has opened the
   * document, so evaluation happens on first open (ADR-004). `revision` records
   * which version of the lexicon was applied, so a reviewer can see they
   * applied v3 while settings now holds v5.
   */
  appliedLexicons?: AppliedLexiconRecord[];

  /**
   * Lexicon-sourced detections restored from the saved asset, whole.
   *
   * ── Correction to the approved Functional Design (FD-1) ────────────────────
   *
   * The design specified a `lexiconDetectionState?` carrying triage state only
   * (id, snapshot, checked, redactedMatchIds, deletedMatchIds, redactionCode),
   * mirroring `detectionState`. That is not sufficient, and shipping it would
   * have produced a defect:
   *
   * PII detections can persist selection state alone because their GEOMETRY is
   * re-fetched from the engine-results asset at every open. A lexicon detection
   * has no such asset. So triage-state-only persistence leaves two options on
   * reopen, and both are wrong:
   *
   *   - Do not re-evaluate: occurrences are empty, so nothing renders and —
   *     worse — `buildRedactionPlan` emits nothing, meaning a document that
   *     was previously exported with lexicon redactions burns in WITHOUT them.
   *   - Re-evaluate against the live lexicon: editing or deleting a lexicon
   *     then reaches back into an already-applied document, which is exactly
   *     what `U5-FR2` / BR-44 forbid.
   *
   * So the geometry is persisted, and reopening reconstructs the detections
   * without consulting any lexicon. This also follows the repo's existing
   * precedent for glyph-anchored work: `keyword` and `text-select` redactions
   * persist their `points` rather than re-running a search on open.
   */
  lexiconDetections?: LexiconSourcedDetection[];
  /**
   * Watermarks applied to the document (VE-24693). Snapshots copied from the
   * library so they survive reopen and are independent of the library entry
   * (AC#3). Optional so older saves (and text-only sessions) round-trip.
   */
  watermarks?: AppliedWatermark[];
  /** See `SessionState.watermarksOff`. */
  watermarksOff?: boolean;
  /**
   * Bates stamping settings for the document (VE-24447). Optional, so saves
   * written before this existed round-trip unchanged and `schemaVersion` stays
   * at 1 — the same additive treatment `piiMatches` and `watermarks` had.
   */
  batesStamp?: BatesStampSettings;
  /** See `SessionState.batesStampOff`. */
  batesStampOff?: BatesStampSettings;
}

/**
 * One persisted PII match — the per-match record stored under each page
 * bucket's `PII` array in the redaction-data asset. Restores which PII
 * matches the user added / redacted across reload, keyed by vendor match id.
 *
 * Presence of an entry means the match was added to the Redactions list; its
 * `redacted` flag is whether that specific match is burned in. Deleting a
 * match normally removes its entry entirely; the optional `deleted` flag is an
 * alternate representation for the partially-deleted-group case.
 */
export interface PersistedPiiMatch {
  /**
   * Vendor match id — links back to the vtn-standard PII result where the rest
   * of the detection info is loaded.
   */
  id: string;
  /** Vendor group id — links every match on the same PII card. */
  groupId: string;
  /** Whether this specific match is redacted (burned in). */
  redacted: boolean;
  /**
   * Alternate way to mark a deleted match without dropping its entry — useful
   * when only part of a group has been deleted. Normally a delete just removes
   * the entry, so this stays undefined.
   */
  deleted?: boolean;
  /** Redundant with the vtn-standard result; kept for clarity / debugging. */
  piiType: PiiTypeId;
  /** Matched text — redundant but kept for clarity. */
  text: string;
  /** 1-based page (redundant with the page bucket key). */
  page: number;
  /** Per-match redaction code (denormalized for clarity). */
  redactionCode?: RedactionCodeAssignment;
  /** Only written in future when a match's box is resized. */
  boundingPoly?: PersistedBoundingPoly;
}

/**
 * Lifecycle of the document PII detection engine for this session:
 *   - `checking`  — on open, still checking the server for a running job /
 *     existing results (the PII subtab shows a loading spinner). Resolves to one
 *     of the states below once the shared engine-status poller's first reading
 *     lands.
 *   - `not-run`   — no PII results; the PII subtab shows "Run PII Detection".
 *   - `processing`— a detection job is in flight (optimistically set on click).
 *   - `done`      — results are available (detections present).
 */
export type PiiEngineState =
  | 'not-run'
  | 'checking'
  | 'processing'
  | 'done'
  /** The task ended in a terminal non-complete state. */
  | 'failed'
  /** Unfinished 12h after it was created; see `TASK_STALL_MS`. */
  | 'timed-out';

/** Watermark layer kind — each applied instance is one or the other. */
export type WatermarkType = 'text' | 'image';

/**
 * Max fill opacity for any watermark layer (VE-24693, AC#8). Capped below 1 so
 * a watermark can never fully cover — and hide — the text underneath it in the
 * redacted output.
 */
export const MAX_WATERMARK_OPACITY = 0.8;

/** Max characters for a text watermark (VE-24693, AC#2). */
export const MAX_WATERMARK_TEXT_LENGTH = 50;

/**
 * Bates stamping settings for one document (VE-24447, U3-FR2).
 *
 * PER DOCUMENT, deliberately — unlike a watermark, which is a reusable library
 * definition applied to many documents. A Bates sequence belongs to the
 * document it numbers: its start number is a fact about this production, not a
 * preset. So this lives on the session and in the redaction-data asset, and
 * never in the watermarks library.
 *
 * It reuses the watermark ANCHORS (`WatermarkAnchorH`, and the top/bottom half
 * of `WatermarkAnchorV`) because U3-FR3's "left/right/center × top/bottom" is
 * exactly that vocabulary, and the burn-in worker resolves both through the one
 * `anchoredCenterPt` implementation — sharing the arithmetic is the point.
 */
export interface BatesStampSettings {
  /**
   * Zero-pad width, e.g. 6 -> "000123". A MINIMUM, not a maximum: a sequence
   * that outgrows it keeps counting rather than truncating, because losing a
   * digit would make two pages share a number.
   */
  digits: number;
  /**
   * Number of ORIGINAL page 1. Every page carries the number of its original
   * position, so a deleted page leaves a gap in the sequence.
   */
  startNumber: number;
  prefix?: string;
  suffix?: string;
  /** Horizontal anchor within the header/footer band. */
  anchorH: WatermarkAnchorH;
  /**
   * Header or footer. Narrower than `WatermarkAnchorV` on purpose — U3-FR3 says
   * the stamp is always in the header or footer, so `'middle'` is not offered.
   */
  anchorV: 'top' | 'bottom';
  /**
   * Absolute point size. Absolute, where a watermark's size is a fraction of
   * page width: production conventions specify a point size, and an export
   * mixing page sizes must not mix stamp sizes.
   */
  fontSize: number;
  /** Hex "#RRGGBB". Absent reads as black — this has to read as document text. */
  color?: string;
  bold?: boolean;
  /**
   * Margins from the page edge, in PDF POINTS: `insetXPt` from the left/right
   * edge, `insetYPt` from the top/bottom. Either absent reads as
   * `BATES_INSET_PT` (0.5in, the production convention) — which is how every
   * stamp saved before these existed reads — so resolve them through
   * `batesInsetXPt`/`batesInsetYPt` rather than defaulting at each call site.
   *
   * Two, because a production may specify a different side margin from its
   * header/footer margin. Equal values ARE the panel's "linked" state; the link
   * is a control, not a stored fact, so nothing here records it.
   *
   * Points, not inches, because that is the unit the page itself is measured
   * in and the unit both the worker and the preview place it with; the panel's
   * controls are the only thing that speaks inches.
   */
  insetXPt?: number;
  insetYPt?: number;
}

/** Point size a new Bates stamp starts at. */
export const DEFAULT_BATES_FONT_SIZE = 10;

/** Padding width a new Bates stamp starts at (the common production default). */
export const DEFAULT_BATES_DIGITS = 6;

// The BOUNDS (max digits / start number / font size / affix length) live in
// `./batesLabel`, the leaf the burn-in worker can also reach — they have to be
// enforced on the load path, and this module is not importable from the worker.

/** The settings a freshly-opened Bates dialog shows. */
export const defaultBatesStampSettings = (): BatesStampSettings => ({
  digits: DEFAULT_BATES_DIGITS,
  startNumber: 1,
  anchorH: 'right',
  anchorV: 'bottom',
  fontSize: DEFAULT_BATES_FONT_SIZE,
  // Bold by default: an identifier has to survive a dark or noisy scan and a
  // photocopy, and Helvetica-Bold does so better. Written in, unlike the fields
  // below, because ABSENT `bold` still reads as regular weight — stamps saved
  // before this default keep the weight they were applied with.
  bold: true,
  // `color` and the two margins stay ABSENT, which is the point: absent already
  // means black and `BATES_INSET_PT`, so writing those values in would add
  // three fields that each say what their own absence says — and would leave
  // two answers to "what does a new stamp look like" that could drift apart.
  // Black because the number is the identifier the production is cited by: too
  // dark is never a defect for it, while too light can cost it its function on
  // a photocopy or a re-scan. The shade control is how an operator asks for
  // less than that.
});

/**
 * A single watermark applied to the current document (VE-24693). Documents can
 * carry several (AC#5), each either a styled text stamp or an uploaded
 * image/logo (`type`). Placement is either `preset` — centered on the page — or
 * `free`, an explicit `x`/`y` the user set by dragging the overlay (AC#7), with
 * `scale` sizing it either way.
 *
 * The fields are a snapshot COPIED from the library `WatermarkDefinition` at the
 * moment it is applied — the doc keeps its own copy, so editing or deleting the
 * library entry never changes documents already using it (AC#3).
 */
export interface AppliedWatermark {
  /** Unique per applied instance (uuid; generated at the dispatch site). */
  instanceId: string;
  /**
   * Source library watermark id, or null for an ad-hoc/in-editor watermark.
   *
   * Persisted in TWO places, which is why the rename from `definitionId` had to
   * carry a compatibility read in both: this asset body (folded in by
   * {@link watermarksFromAssetBody}) and the `document-watermark-image` asset's
   * `details` (folded in by `serviceFindWatermarkImageAsset`). The second is the
   * one that bites — it is the key that makes re-applying a watermark
   * idempotent, and a miss there is silent: the raster simply uploads again.
   */
  watermarkId: string | null;
  type: WatermarkType;
  /** Fill opacity, 0..MAX_WATERMARK_OPACITY. */
  opacity: number;
  /**
   * Rotation in degrees, COUNTER-clockwise — the direction the preview and
   * the worker actually agree on (`rotate(-rotation)` in y-down CSS, and
   * `[cos, sin, -sin, cos]` in y-up PDF space are the same turn). This said
   * clockwise for a while and neither side ever did that.
   */
  rotation: number;
  /**
   * `preset` anchors the mark to the page (see {@link anchorH}); `free` places
   * it at `x`/`y`, set once the user drags it. Size comes from `scale` in both
   * cases.
   */
  placement: 'preset' | 'free';
  /**
   * "Position on Screen" for a PRESET-placed mark — which point of the page it
   * is pinned to. Absent reads as centre/middle, which is what every mark
   * written before this existed was.
   *
   * Ignored entirely once `placement` is `free`: dragging a mark means the
   * operator has chosen a spot, and re-anchoring it afterwards would move it
   * out from under them.
   */
  anchorH?: WatermarkAnchorH;
  anchorV?: WatermarkAnchorV;
  /**
   * "Inset from edge" for an edge-anchored mark, as a fraction of the SMALLER
   * page dimension. Absent reads as 0 (flush). Only edge anchors use it.
   */
  inset?: number;
  /**
   * A custom position for a PRESET-placed mark, as a fraction of page width /
   * height. Set by dragging in the settings preview, where there is no real
   * page to give points against.
   *
   * Distinct from `x`/`y`, which are POINTS on a real page and come with
   * `placement: 'free'` once the operator drags the mark in the editor. Both
   * exist because they answer different questions: this one travels with a
   * definition to pages of unknown size, that one pins a mark to the page in
   * front of you. `x`/`y` win — a drag on the real document is the more
   * specific instruction.
   */
  customXPct?: number;
  customYPct?: number;
  /**
   * Alignment of the LINES within a multi-line block. Absent reads as centre.
   * Does nothing on a single line — the block is as wide as the line.
   */
  align?: WatermarkTextAlign;
  /**
   * Draw the text in bold. An ordinary flag: absent reads as false.
   *
   * It briefly read as TRUE, because text was drawn bold on both sides before
   * the toggle existed. The factories set it explicitly instead, so a new mark
   * is still bold without the reader having to invert the usual rule.
   */
  bold?: boolean;
  /**
   * Free-placement X in PDF points, TOP-LEFT page origin; null in preset.
   *
   * What the point MEANS differs by type, and this is not cosmetic — read it
   * wrong and the mark is off by half its own width: a TEXT watermark stores
   * its CENTER here, an IMAGE stores its TOP-LEFT corner. `watermarkGeometry`
   * and the burn-in worker both branch on it.
   */
  x: number | null;
  /** Free-placement Y, same origin and same per-type meaning as `x`. */
  y: number | null;
  // --- text layer ---
  /** Watermark text (e.g. "CONFIDENTIAL", a case number, a date). */
  text: string;
  /**
   * Hex color, `#RGB` or `#RRGGBB`, normalized by {@link normalizeWatermarkColor}
   * on the way in — see there for why an arbitrary string is not acceptable.
   *
   * For TEXT this is the color the glyphs are drawn in, and it is always set.
   * For an IMAGE it is an optional TINT, and the empty string means "no tint,
   * keep the upload's own colors". Empty rather than black: `deriveWatermarkImage`
   * reads any truthy color as a tint to apply, so a black default multiplied
   * every pixel by [0,0,0] and turned the logo into a silhouette.
   */
  color: string;
  /**
   * Render this watermark in grayscale (ticket amendment 2026-08-17,
   * which gives BOTH capabilities to BOTH types: color and grayscale). For an image
   * the drawn PNG is a grayscale derivation of `imageBaseDataUrl`; for text the
   * drawn color is the grayscale equivalent of `color`. Reversible either way —
   * the color source is always kept.
   */
  grayscale: boolean;
  // --- image layer ---
  /**
   * Base64 data URL of the image AS DRAWN — the base, optionally grayscaled
   * (`grayscale`) and/or tinted to `color`. This is what the preview and
   * burn-in use directly, so no color work happens at export time.
   *
   * SESSION-ONLY: derived on open and on every colour change, never persisted
   * (`watermarksForStorage` nulls it) because it is a pure function of the base
   * plus the flags.
   */
  imageDataUrl: string | null;
  /**
   * Base64 data URL of the rasterized upload with its ORIGINAL COLOURS, kept so
   * grayscale and tint can be turned on and off without re-uploading. Null when text.
   *
   * ALSO SESSION-ONLY, as of asset format 1.3.0. The raster itself now lives in
   * its own `document-watermark-image` asset on the TDO and this field is
   * populated by fetching {@link imageAssetId} once when the document opens. It
   * used to be persisted inline, which put ~40k characters of base64 in the
   * middle of a redaction-data body and made the body unreadable — a real cost
   * for a file that gets opened by hand whenever a redaction is disputed.
   */
  imageBaseDataUrl: string | null;
  /**
   * Id of the `document-watermark-image` asset holding this mark's raw raster,
   * or null for a text mark (and for an image mark whose upload has not been
   * stored yet — the upload is async, so a mark exists briefly without one).
   *
   * THE PERSISTED FORM of an image mark's artwork. The asset is IMMUTABLE and
   * written once per (document, watermark): none of the mutable attributes —
   * grayscale, tint, scale, opacity, rotation, placement — live in it, so no
   * settings tweak rewrites it. That immutability is what preserves AC#3: the
   * id resolves to exactly the artwork that was applied, whatever later happens
   * to the library definition, including a permanent delete.
   */
  imageAssetId: string | null;
  /** Original filename, shown in the UI. */
  imageName: string | null;
  /**
   * Rasterized height / width of the image, captured at upload. Carried on the
   * watermark so the overlay can lay it out without decoding the image again at
   * render time. 1 for text, or for an image whose aspect was never recorded.
   */
  imageAspect: number;
  /**
   * Size as a share of the page width, 0..1 — the size of an IMAGE mark.
   *
   * The only representation of an image's size: it used to compete with
   * `width`/`height`, and a panel that wrote one while the export read the
   * other is how a dragged mark came to burn in at the wrong size and place.
   *
   * It carried text marks too until VE-24693 review round 2. Still written for
   * them, and still READ for a text mark saved before `fontSizePt` existed —
   * see {@link fontScaleOf} — but it is no longer what a text mark means.
   */
  scale: number;
  /**
   * Size of a TEXT mark, as a coefficient of page width applied to the FONT
   * SIZE: `fontSizePt = fontScale x pageWidthPt`. Null for an image, and null
   * on a text mark stored before this field existed.
   *
   * Both types are sized by a coefficient of page width, but they multiply
   * different things — `scale` is an image's WIDTH, `fontScale` is text's FONT
   * SIZE — which is why they are two fields and not one. Conflating them is the
   * mistake this pair exists to make impossible.
   *
   * The distinction matters because the old shared field meant "width of the
   * whole string" for text, and the font size was back-derived by dividing by
   * character count: the same setting rendered "OK" at 306pt and a 34-character
   * line at 18pt. Nothing about a watermark's size should track how much text
   * it has.
   *
   * Never read directly; {@link fontScaleOf} owns the legacy conversion.
   */
  fontScale: number | null;
}

/** Clamp a watermark opacity into the legal 0..MAX_WATERMARK_OPACITY range. */
export function clampWatermarkOpacity(o: number): number {
  if (!Number.isFinite(o)) {
    return 0.3;
  }
  return Math.max(0, Math.min(o, MAX_WATERMARK_OPACITY));
}

/** `#RGB` or `#RRGGBB`, and nothing else. */
export const WATERMARK_HEX_REGEX = /^#([0-9a-f]{3}){1,2}$/i;

/**
 * Coerce a watermark color into a hex literal, falling back when it is anything
 * else.
 *
 * This is a security boundary, not tidiness. A watermark's `color` arrives from
 * two stores any org member can write through the public GraphQL API — the
 * watermark SDO registry and a document's redaction asset — and it is rendered
 * by interpolating it into a CSS declaration value (MUI `sx`, and the overlay's
 * inline style). Neither MUI nor emotion escapes a value: a color of
 * `red}*{background-image:url(https://attacker.example/beacon)` closes the
 * generated rule and opens one with an attacker-chosen selector, so a poisoned
 * library entry fires a request from every colleague who merely OPENS the
 * settings page. There is no CSP behind this to blunt it.
 *
 * Removing one interpolation site is not the fix — the same class already came
 * back once on a different property. The fix is that no unvalidated string is
 * ever a color, which is why this sits in the factories every instance is built
 * through, and at the fetch boundary for definitions that are rendered directly.
 */
export function normalizeWatermarkColor(
  value: unknown,
  fallback: string
): string {
  return typeof value === 'string' && WATERMARK_HEX_REGEX.test(value)
    ? value
    : fallback;
}

/**
 * The color a text watermark is actually DRAWN in. Grayscale desaturates
 * the chosen color to its grey rather than forcing pure black, so text behaves
 * the same way `toGrayscale` treats image pixels. Single source for the editor
 * overlay and the burn-in payload, so the preview can't drift from the export.
 */
export function drawnTextColor(
  wm: Pick<AppliedWatermark, 'color' | 'grayscale'>
): string {
  return wm.grayscale ? grayscaleHex(wm.color) : wm.color;
}

/** Build a text watermark instance with sensible defaults (VE-24693). */
export function createTextWatermark(
  instanceId: string,
  over: Partial<AppliedWatermark> = {}
): AppliedWatermark {
  return {
    instanceId,
    watermarkId: null,
    type: 'text',
    rotation: 0,
    placement: 'preset',
    anchorH: 'center',
    anchorV: 'middle',
    inset: 0,
    align: 'center',
    bold: true,
    x: null,
    y: null,
    text: 'CONFIDENTIAL',
    grayscale: false,
    imageDataUrl: null,
    imageBaseDataUrl: null,
    imageAssetId: null,
    imageName: null,
    imageAspect: 1,
    scale: DEFAULT_WATERMARK_SCALE,
    fontScale: DEFAULT_FONT_SCALE,
    ...over,
    // The palette's red (#D32F2F), not pure #FF0000. They look alike and are
    // not the same value: #FF0000 is in no preset, so an ad-hoc text mark
    // opened in the editor with its CUSTOM slot lit while a mark from the
    // drawer — whose new-form default is #D32F2F — lit the red preset. Same
    // colour by eye, two different states in the picker, which is what made
    // the two swatch rows look like different palettes.
    color: normalizeWatermarkColor(over.color, '#D32F2F'),
    opacity: clampWatermarkOpacity(over.opacity ?? 0.3),
  };
}

/** Build an image/logo watermark instance with sensible defaults (VE-24693). */
export function createImageWatermark(
  instanceId: string,
  over: Partial<AppliedWatermark> = {}
): AppliedWatermark {
  return {
    instanceId,
    watermarkId: null,
    type: 'image',
    rotation: 0,
    placement: 'preset',
    anchorH: 'center',
    anchorV: 'middle',
    inset: 0,
    align: 'center',
    bold: true,
    x: null,
    y: null,
    text: '',
    grayscale: false,
    imageDataUrl: null,
    imageBaseDataUrl: null,
    imageAssetId: null,
    imageName: null,
    imageAspect: 1,
    scale: DEFAULT_WATERMARK_SCALE,
    // An image is sized by `scale`; a text coefficient here would be a second
    // answer to a question this mark does not ask.
    fontScale: null,
    ...over,
    // '' = keep the upload's own colors. See the `color` field doc.
    color: normalizeWatermarkColor(over.color, ''),
    opacity: clampWatermarkOpacity(over.opacity ?? 0.15),
  };
}

/**
 * The full editor session held in `state.documents.session`.
 */
export interface SessionState {
  tdoId: TDOId;
  documentName: string;
  pdfUrl: string;
  /**
   * The `media` asset `pdfUrl` was signed from.
   *
   * Pinned for as long as the document stays on screen: OCR republishes a
   * searchable copy as a new primary asset while the document may be open, and
   * the session deliberately keeps reading the one it opened rather than
   * swapping a file under work in progress. The refresh prompt
   * (`OCR_REFRESH_AVAILABLE` → `RELOAD_FOR_OCR`) is what moves it forward.
   *
   * The ONE place it moves on its own is a restored suspended session: coming
   * back to a parked document re-resolves the primary asset and adopts it, so a
   * document parked mid-OCR returns searchable instead of returning to the
   * pre-OCR file with the UI claiming OCR finished. The redactions are
   * unaffected — the enriched copy keeps the original's page geometry. See the
   * restore branch of `OPEN_DOCUMENT_SUCCESS`.
   */
  mediaAssetId: string | null;
  /**
   * The engine that produced `mediaAssetId`, or null for the uploaded original.
   *
   * Exists to answer one question the OCR watch cannot answer from task status:
   * IS THE FILE ON SCREEN OCR'S OWN OUTPUT? A finished OCR task only means this
   * document is searchable when it is — otherwise OCR's copy is a different
   * file, and the reviewer has to be offered the reload rather than told that
   * search works.
   *
   * The watch used to infer this from "is this my first reading?", which is a
   * proxy, not the fact: OCR can finish in the window between `openDocument`
   * resolving this asset and the watch's first tick, and then the first reading
   * is already terminal over a file that predates the searchable copy. The UI
   * reported OCR complete, the manifest suppressed the per-page "no searchable
   * text" notice, and the reviewer searched a document with no text layer.
   *
   * Null is read as "not OCR's output" — the direction that offers a reload
   * that may be unnecessary rather than claiming a document is searchable when
   * nothing establishes that it is. The offer also requires the primary to
   * have moved off `mediaAssetId` (VE-29505).
   */
  mediaAssetEngineId: string | null;
  totalPages: number;

  /**
   * Document PII detection engine lifecycle (VE-21915). Initialized from
   * whether detections are present at open; flipped to `processing` when the
   * user triggers a re-run via the PII subtab button.
   */
  piiEngineState: PiiEngineState;

  /**
   * Redact Document OCR engine lifecycle for this document.
   * `checking` on open until the task/manifest check resolves it.
   */
  ocrEngineState: OcrEngineState;

  /**
   * The `redact-document-ocr-manifest` asset for the document now open, or null
   * when there is none — which is the normal state for anything ingested
   * before the OCR engine existed, not an error. Null must therefore read as
   * "nothing known about OCR here", never as "OCR found nothing".
   */
  ocrManifest: OcrManifest | null;

  /**
   * Whether the OCR-flagged region overlay is on. Document-wide,
   * like `ocrWarningDismissed`, so toggling it from the document-level notice
   * applies to every page. Off by default: the boxes are review aids, not
   * something to look at while redacting. Not persisted.
   */
  showOcrRegions: boolean;

  currentPage: number;
  /**
   * Increments on every SCROLL_TO_PAGE dispatch (VE-21915). The canvas
   * scroll-into-view effect keys off this rather than `currentPage` alone, so
   * navigating to the page you're already "on" (e.g. clicking a card after
   * scrolling away — the IntersectionObserver only tracks a local visible
   * page, not `currentPage`) still scrolls.
   */
  scrollToken: number;
  /**
   * Vertical target (PDF points, top-left origin) for the pending scroll, or
   * null to snap the page top to the viewport top. Set by SCROLL_TO_PAGE so
   * the canvas can center on a clicked redaction/detection's location.
   */
  scrollToY: number | null;
  /**
   * How the canvas should place `scrollToY` in the viewport for the pending
   * scroll. `'center'` (default) centers it — used by card / redaction clicks.
   * `'nearest'` does a minimal scroll, landing the target ~1/3 of the viewport
   * up from the bottom only when it's outside a comfortable band — used by
   * keyword prev/next so stepping through matches doesn't recenter on every
   * click or leave the next match hugging the bottom edge.
   */
  scrollAlign?: 'center' | 'nearest';
  zoom: number;
  zoomMode: ZoomMode;
  activeTool: Tool;
  activeTab: ActiveTab;
  detectionsTab: DetectionsTab;
  redactionsSubtab: RedactionsSubtab;
  /**
   * The editor CHOSE the Detections tab at open, and may still hand it back.
   *
   * Set when a document opens with no redaction work to return to, so the
   * reviewer lands on the tab that can produce something. While it holds, the
   * first manual redaction flips the panel to Redactions — the reviewer has
   * started producing work, and that is where it lives.
   *
   * Cleared by any interaction with the right panel, because at that point
   * the reviewer has chosen what they are looking at and the editor moving it
   * underneath them is worse than a stale tab. Also cleared by the flip
   * itself, so it happens at most once per open.
   */
  autoLandedOnDetections: boolean;

  detections: Detection[];
  /**
   * Lexicons applied to this document (VE-24948 / U5), restored at open and
   * appended to as the reviewer applies more. Persisted on the asset.
   */
  appliedLexicons: AppliedLexiconRecord[];
  /** Progress/outcome of lexicon application in this session. Not persisted. */
  lexiconApply: LexiconApplyState;
  /**
   * Per-match PII records loaded from the saved asset, stashed here at open so
   * they can be re-applied to engine detections that arrive asynchronously via
   * PII_RESULTS_LOADED (the results-load saga runs after OPEN_DOCUMENT_SUCCESS).
   * Empty when the document has no saved PII selections.
   */
  persistedPiiMatches: PersistedPiiMatch[];
  redactions: Redaction[];
  detectionRectOverrides: Record<DetectionRectKey, BoundingBox>;
  /**
   * Pages the user marked with the tool the UI calls **Remove Page**. The page
   * still ships in the output, but its content is wiped and replaced with the
   * slip-sheet notice, so page numbering is preserved. Persisted to the
   * redaction-data asset via the `pageContentRemoved: true` flag.
   *
   * The field name predates the rename (the tool used to be "Redact Page");
   * the wire format and UI both say "removed" now.
   */
  pageRedacts: number[];
  /**
   * Pages the user marked with the tool the UI calls **Delete Page**
   * (VE-20838), gated behind the `enablePageDeletion` flag (default ON).
   * Distinct from `pageRedacts`: a
   * hidden page is dropped from the PDF entirely, so the output has fewer
   * pages than the source. Honored by the burn-in worker
   * (`burnInWorker.deletePage`) and persisted via the `pageHidden: true` flag.
   *
   * The field name predates the rename (the tool used to be "Remove Page").
   */
  excludedPages: number[];

  /**
   * Set of currently-selected redaction ids. Array (not nullable scalar) so
   * shift+click / marquee can extend the selection. Single-click without a
   * modifier replaces the set with one id; clicking empty canvas clears it.
   * Length-1 is the common case and matches the prior single-select UX.
   */
  selectedRedactionIds: string[];
  /**
   * Set of currently-selected PII detection occurrences, keyed as
   * `${detectionId}|${page}`. Parallel to `selectedRedactionIds` —
   * PII overlays participate in shift+click and marquee multi-select
   * but live on `detections` (not `redactions`) so they need their
   * own selection set. Replaced when a redaction click happens
   * without a modifier (and vice versa) so the user sees one
   * "active" selection at a time.
   */
  selectedDetectionRowKeys: string[];
  selectedDetectionMaskKey: DetectionRectKey | null;

  keywordSearch: {
    phrase: string;
    matches: KeywordMatch[];
    lastSearchedAt: number;
    /** Match-case toggle (VSCode-style `Aa` button). Default false. */
    matchCase: boolean;
    /** Whole-word toggle (VSCode-style `ab|` button). Default false. */
    wholeWord: boolean;
  };
  detectionSearch: string;

  /**
   * Lexicon ids the reviewer has UNCHECKED in the Categories dropdown
   * (VE-24948 / U5). Absent id means visible.
   *
   * Stored as the exceptions, not as the whole list, and in redux rather than
   * component state. As component state it was initialised empty and filled by
   * an effect, so on every remount — which the detections panel's own header
   * documents as routine when the redacted-file tab is shown — no lexicon id
   * was active for a frame and every lexicon row was filtered out before
   * flashing back in; and the reviewer's un-check did not survive that
   * remount, unlike `detectionsTab`, which is reconstructed from redux for
   * exactly this reason (review-gate M5). Keeping only the exceptions means a
   * newly applied lexicon is visible by default with no reconcile step.
   */
  lexiconFilterUnchecked: string[];
  detectionFilter: { direct: PiiTypeId[]; indirect: PiiTypeId[] };
  /** The Detections tab's source strip (VE-30011) — see `DetectionSource`. */
  detectionSource: DetectionSource;

  undoStack: UndoState;

  save: SaveState;

  /** Burn-in / export lifecycle (VE-20837). */
  export: ExportState;

  /**
   * Watermarks applied to this document (VE-24693). Zero or more, each a text
   * stamp or an image/logo, burned into every surviving page of the export and
   * shown as a live editor-preview overlay. Each entry is a snapshot copied
   * from a library `WatermarkDefinition` (or ad-hoc), so editing/deleting the
   * library entry never changes docs already using it (AC#3).
   */
  watermarks: AppliedWatermark[];
  /**
   * The watermark panel's switch, when it is OFF: the marks are kept — listed,
   * saved, and back the moment it is turned on — but nothing draws on the page
   * or burns into the export. It used to be implemented by emptying
   * `watermarks`, which made "off" a delete.
   */
  watermarksOff: boolean;

  /**
   * Instance ids whose stored artwork could not be fetched back THIS open
   * (VE-28770).
   *
   * Session-only, and deliberately not written to the mark or the asset: it
   * records what this open could not read, which is not the same as a fact
   * about the document. A network blip on the asset fetch is indistinguishable
   * here from an asset that is genuinely gone, so the mark KEEPS its
   * `imageAssetId` — clearing it would turn one bad request into permanent
   * data loss on the next save, and the next open may well resolve it.
   *
   * What it buys is the distinction the panel cannot otherwise make: "nothing
   * is coming" versus "not here yet". Both look like an image mark with no
   * raster, and the panel used to show the library's thumbnail for both — a
   * healthy-looking row for a mark that exports nothing.
   */
  watermarkImageRestoreFailures: string[];

  /**
   * Bates stamping applied to this document (VE-24447), or null when the user
   * has not set one up. Burned into every surviving page at export; unlike a
   * watermark its canvas preview is not interactive: there is nothing to
   * drag, because its position is one of six anchors chosen in the panel.
   */
  batesStamp: BatesStampSettings | null;
  /**
   * The settings KEPT while Bates numbering is switched off, or null. Never
   * set at the same time as `batesStamp`: that field stays "the numbering
   * this document is stamped with", so the export, the preview and the audit
   * trail read it alone and need not know this exists. This is only what the
   * panel reopens with — turning numbering off used to clear `batesStamp`, and
   * with it everything the reviewer had set up.
   */
  batesStampOff: BatesStampSettings | null;

  /**
   * Whether the watermark panel is open (VE-24693). Gates on-canvas drag/resize
   * of watermarks (AC#7) so they only intercept the pointer while the user is
   * positioning them — otherwise they'd block redaction drawing.
   *
   * The panel's open state, not a separate "edit mode": it used to be both, a
   * local `useState` in DocumentEditorView deciding whether to mount the panel
   * and the panel then dispatching this from an effect, so two things could
   * disagree about one editor mode and nothing else could ask.
   */
  watermarkEditing: boolean;

  /**
   * Which applied watermark is selected (VE-24693), or null.
   *
   * In the store because two components own halves of the same interaction: the
   * panel lists the marks and shows the selected one's controls, while the
   * canvas overlay draws its handles. They each kept their own `selectedId`, so
   * clicking a row put no handles on the canvas and clicking a mark revealed no
   * sliders — in a panel whose whole premise is that positioning happens on the
   * page. View-only: not undoable, does not dirty the document.
   */
  selectedWatermarkId: string | null;

  /**
   * Redaction status identity stored on the TDO's `details.tags` under
   * `IN_REDACTION_TAG_KEY` (VE-21914): `redactionStatusKey ?? redactionStatus`.
   * Canonical value (built-in filter like `'draft'`, or a custom `statusKey`
   * UUID) — mirrors the value `MediaDetailsTopBar` reads/writes for the video
   * flow so a status changed in either editor is visible in the other.
   *
   * `null` when the open hasn't finished yet (rare; cleared on CLOSE).
   * Persistence is via `serviceUpdateTdoRedactionStatus` — the doc editor
   * doesn't share the `mediaDetails` slice, so we own a parallel write path
   * that targets the same TDO field.
   */
  redactionStatus: string | null;

  /**
   * Human-readable name frozen on the tag's `redactionStatus` (VE-24546). Used
   * only to display a *deleted* custom status by its last-known name instead of
   * "Unknown"; live statuses resolve their label from `redactionStatus`
   * (identity) via the org's custom statuses. `null` when none was stored.
   */
  redactionStatusName: string | null;

  versionHistory: RedactedVersion[];

  /**
   * Pages a burned-in output ACTUALLY dropped, keyed by that output's
   * `versionId`. Read back from the `document-redaction-data` asset the burn-in
   * consumed, so it reflects what the redacted file contains — not
   * `session.excludedPages`, which is the live editor state and drifts as soon
   * as the user deletes or restores a page after the last burn-in.
   *
   * Side-by-Side View needs this to line the two columns up: mapping original
   * pages onto redacted ones with the editor's list mis-registers every page
   * after the first divergence.
   *
   * Loaded lazily (Side-by-Side open) and cached per version; a missing entry
   * means "not loaded or unavailable", NOT "nothing was deleted" — consumers
   * fall back to `excludedPages`. Not persisted.
   */
  redactedExcludedPagesByVersion: Record<string, number[]>;

  /**
   * Vendor "Send to" request-ids read from the TDO's `details` at open
   * (VE-23442). A non-null id enables the matching button on the Redacted
   * File tab. All null when the TDO carries no integration request-ids.
   */
  vendorRequestIds: VendorRequestIds;
  /**
   * Parent folder (case) identity for the generic external-integration send,
   * used to fill the `{{{folderId}}}` / `{{{folderName}}}` template slots.
   * Null when the document isn't opened under a case folder — the send still
   * proceeds with empty folder context (mirrors VE-23386 for video).
   */
  folderId: string | null;
  folderName: string | null;

  // Populated by DocumentCanvas as each page renders; keyed by 1-based page number.
  // Used by the keyword search saga. Not persisted.
  pageTexts: Record<number, string>;

  // Per-page searchability captured at render time (VE-23390); keyed by
  // 1-based page number. Drives the "some/all pages can't be searched"
  // warnings on the search bar. Not persisted.
  pageSearchability: Record<number, 'searchable' | 'unsearchable' | 'blank'>;

  // Whether the user dismissed the per-page "no searchable text" notice
  // (VE-23390). Document-wide so dismissing on one page collapses it on all
  // pages. Not persisted.
  ocrWarningDismissed: boolean;

  // Whether the user dismissed the per-page "processed by OCR" notice.
  // Document-wide, like the flag above, and DELIBERATELY SEPARATE from it: one
  // is an acknowledgement ("yes, I know these pages were scanned") and the
  // other a caution about what search cannot reach. Collapsing the first must
  // not silence the second — a reviewer who has taken the point about
  // provenance has not thereby waived the warning. Not persisted.
  ocrProcessedDismissed: boolean;

  // True once the open-time pre-pass (pdfPagePrepass → SET_PAGE_TEXT_BATCH) has
  // finished indexing EVERY page's text. Until then keyword search only covers
  // the pages the render path has backfilled, so results on a long document are
  // partial — the search rail shows a spinner while this is false. Reset on
  // OPEN_DOCUMENT_SUCCESS, set by PDF_TEXT_PREPASS_DONE (fires on pre-pass
  // completion OR failure, so it never sticks; not on cancellation, whose
  // session is gone or replaced). Not persisted.
  textIndexReady: boolean;
  // The pre-pass failed: pages past the failure may have no text (VE-29502).
  // Includes a stream the extractor gave up on part-way or before its first
  // range, which ends without throwing (VE-29686).
  textIndexFailed?: boolean;

  // Streaming pre-pass progress: `indexed` of `total` pages extracted so far, or
  // null before it starts. Drives the search rail's "Indexing document — X of Y
  // pages" indicator (VE-26267). `total` is also the page count lexicon apply
  // names unindexed pages against, since `totalPages` is only a seed of 1
  // (VE-29686). Not persisted.
  textIndexProgress: { indexed: number; total: number } | null;

  /**
   * True once the scan that separates a SCANNED page from a blank one has
   * finished — the raster probe forked after the text pre-pass, not the text
   * pre-pass itself.
   *
   * Until this flips, `pageSearchability` holds the pre-pass's provisional
   * `blank` for every text-less page, so "no page is marked unsearchable" means
   * NOBODY HAS LOOKED rather than "nothing needs OCR". Anything deciding
   * whether the document needs OCR has to wait for this, not for
   * `textIndexReady`. Reset on OPEN_DOCUMENT_SUCCESS, set by
   * PAGE_SEARCHABILITY_SCAN_DONE (which fires on completion, on nothing-to-do
   * and on failure, so it never sticks). Not persisted.
   */
  searchabilityScanReady: boolean;

  /**
   * `scanned` of `total` text-less pages probed so far, or null before the
   * probe pass starts. `total` counts the text-less pages, not the document.
   * Not persisted.
   */
  searchabilityScanProgress: { scanned: number; total: number } | null;

  /**
   * Per-character text geometry, used to resolve character offsets in
   * `pageTexts` back to on-screen rects (VE-20838). Coordinates are PDF
   * user-space points with a top-left origin, page-relative — the frame the
   * MuPDF raster is drawn in, so a box here lands on the glyphs the user sees.
   * Not persisted.
   *
   * CONTRACT: this is MuPDF glyph geometry, ONE ENTRY PER CHARACTER, and the
   * only producer is `toPagePrepassEntry` (the streaming pre-pass, and the
   * render-path backfill that calls `extractSinglePagePrepass`). Do not write
   * pdf.js `getTextContent()` items here. They are multi-character RUNS in a
   * different frame, and both properties break silently: consumers interpolate
   * a sub-run box as `width / length`, which is exact per character and off by
   * a character or two across a run, and pdf.js text-layer positions drift from
   * the raster. That mix is what made redaction geometry depend on which
   * producer reached a page first (VE-27405).
   *
   * `length` must be the character count `buildPageText` contributed for the
   * item — seam-stripped — because every consumer walks `offset += length + 1`
   * over `pageTexts` in lockstep with this array.
   */
  pageTextItems: Record<
    number,
    Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      length: number;
    }>
  >;

  // Every page's native size (PDF points, scale 1.0, /Rotate applied), keyed
  // by 1-based page number. Filled up front by the pdf pre-pass saga
  // (extractPdfPagePrepass) at open, independent of rendering — the editor
  // canvas virtualizes pages, so a page's real dimensions must be known before
  // it renders to reserve the correct scroll height and to size fit-page
  // (VE-25293). Empty until the pre-pass completes; the render path still
  // reports dims per page as a fallback. Not persisted.
  pageDimensions: PageDimensions;
}

// ---------------------------------------------------------------------------
// Persisted asset format (VE-20835)
// ---------------------------------------------------------------------------

/**
 * Pair of coordinates serialized in the persisted asset. Tuple form
 * matches the existing `redaction-media-data` asset convention.
 */
export type PersistedPoint = [number, number];

/**
 * Four corners of an axis-aligned rectangle: (x1,y1), (x1,y2), (x2,y1),
 * (x2,y2). Used only by `PersistedPiiMatch.boundingPoly` (a future
 * resized-PII-box field); redaction entries store their outline as `points`.
 */
export type PersistedBoundingPoly = [
  PersistedPoint,
  PersistedPoint,
  PersistedPoint,
  PersistedPoint,
];

/**
 * Per-redaction entry inside the persisted asset.
 *
 * Geometry is stored as `points` only — the canonical outline. A rectangle is
 * its four corners (clockwise from top-left); a custom polygon is its vertices
 * in order. The axis-aligned bounding box is NOT persisted; it's re-derived
 * from `points` on load (`pointsToBox`). `isCustomPolygon` distinguishes a
 * 4-point rectangle from a 4-point polygon.
 *
 * DOC-TODO: coordinate space is the document editor's current page-pixel
 * space (matches in-memory `BoundingBox`). The ticket flags 0-1
 * normalization as an open question; revisit when the BFF schema is
 * finalized.
 */
export interface PersistedRedactionEntry {
  /**
   * The redaction's outline. Rectangle = 4 corners (TL, TR, BR, BL); custom
   * polygon = ordered vertices. Required — there is no separate bounding-box
   * field; the box is derived from these on load.
   */
  points: PersistedPoint[];
  /** Marks the entry as a free-form polygon (vs an axis-aligned rectangle). */
  isCustomPolygon?: boolean;
  redactionCode?: RedactionCodeAssignment;
  /**
   * VE-20839: when explicitly `false`, the redaction is un-redacted — the
   * editor skips rendering it and burn-in excludes it. Omitted (default) means
   * redacted, so older assets without this field load with their existing
   * behavior. Renamed from `active` to match the PII nomenclature.
   */
  redacted?: boolean;
  /**
   * VE-20839: persisted source phrase for keyword + text-select
   * redactions. Used after reload to power right-click "Delete All
   * (N)" / "Unredact All (N)" + the Redactions subtab grouping.
   *   - keyword origin: always
   *   - text-select origin: always (the prior 10-word cap was a
   *     privacy hedge, but the geometry already pins the redacted
   *     region down in the doc so leaving `phrase` out doesn't
   *     actually hide anything — and long selections need the
   *     phrase too for grouping on reload)
   *   - manual / page-redact: never (no source phrase concept)
   */
  phrase?: string;
  /**
   * Mirrors `Redaction.name`. Persisted so user-edited Region card
   * titles (and the default `Overlay N`) survive save+reload.
   */
  name?: string;
  /**
   * Mirrors `Redaction.textSelectionId`. Persisted so clicking one row of a
   * multi-row stretch of text still selects the whole group after reload.
   * Written on text-select entries and on keyword entries whose match wraps a
   * line (VE-26267) — see `Redaction.textSelectionId` for the per-origin rule.
   * Absent on single-box entries, which are their own group.
   */
  textSelectionId?: string;
  /**
   * Mirrors `Redaction.wordCount`. Number of words this row redacts, derived
   * from `phrase`. Added in asset format 1.0.0; only written on text-select
   * entries. Absent on 0.0.3 assets (treated as 0 on load).
   */
  wordCount?: number;
}

/**
 * `document-redaction-data` asset body. One asset = one snapshot of the
 * full set of saved redactions for a TDO.
 *
 * Shape mirrors the ticket's nested layout:
 *   redactions[page][origin][id] = entry
 *
 * Persisted origins: `manual`, `keyword`, `text-select` (custom polygons
 * live under `manual` with `isCustomPolygon: true`). `page-redact` is tracked
 * via the `pageContentRemoved` flag, not as an entry.
 *
 * `assetFormatVersion` identifies the schema of this asset body. The same
 * value is mirrored into the asset's GraphQL `details` metadata at save
 * time so loaders can filter by format without downloading the body. It
 * is distinct from any document-content version (e.g. RedactedVersion).
 */
export interface DocumentRedactionAssetV1 {
  // Asset body schema version. 0.0.3 = points-only geometry (no stored
  // bounding box) + custom polygons under `manual` via `isCustomPolygon`.
  // 1.0.0 adds per-row `wordCount` on text-select entries (for word-count
  // metrics). 1.1.0 renames the two page-level flags to `pageContentRemoved` /
  // `pageHidden` and dual-writes the 1.0.0 names so a rollback still reads
  // them. 1.2.0 adds the top-level `watermarks` array (VE-24693). 1.3.0 moves an
  // image watermark's raster OUT of this body: the mark persists an
  // `imageAssetId` pointing at its own `document-watermark-image` asset instead
  // of an inline `imageBaseDataUrl`. ~40k characters of base64 per mark made
  // this body unreadable by hand, which is a real cost for a file that gets
  // opened whenever a redaction is disputed. Reading is backward compatible — a
  // 1.2.0 body still carries its base inline and `watermarksFromAssetBody`
  // accepts either — so the bump announces the new shape rather than gating it.
  //
  // 1.4.0 adds the top-level `batesStamp` object (VE-24447). Bumped for the same
  // reason 1.2.0 was, though the key is purely additive: the version tells a
  // reader what shape a body may be in without fetching it out of object
  // storage, and one version string meaning two different shapes cannot do that.
  // It does NOT say whether a given document carries Bates numbering — the
  // version is written on every save regardless, so only the body answers that.
  // Reading stays backward compatible: the key is optional and
  // `isAssetFormatSupported` is a floor check with no ceiling.
  //
  // The stamp's optional `insetXPt`/`insetYPt` (its margins) are part of 1.4.0
  // rather than a 1.5.0 of their own: they were added while 1.4.0 was still
  // unreleased, so no shipped asset can carry a `batesStamp` without them
  // having meant the same thing — both are optional and an absent one resolves
  // to the 0.5in default through `batesInsetXPt`/`batesInsetYPt`. A field added to `batesStamp` AFTER this
  // version ships does need its own bump, by the rule above.
  //
  // 1.5.0 adds `lexiconDetections`, `appliedLexicons` and a per-page `LEXICON`
  // bucket (VE-24948). It has now been renumbered TWICE on merge — 1.2.0 on its
  // own branch, then 1.4.0, then this — because watermarks and then Bates
  // stamping landed first. All three features are independent, so the rule is
  // simply that whoever merges later takes the next number; nothing about the
  // lexicon body changed either time. Optional flags inside `incomplete` (e.g.
  // `indexing`, VE-29502) don't bump it: readers keep the record whole.
  //
  // VE-29686 is the limit of that argument and still does not bump, which is
  // worth stating rather than leaving to be inferred. It adds optional flags
  // (`pageCountUnknown`, `indexFailed`) — additive, covered above — but it also
  // SUPERSEDES one key with another shape: `incomplete.unsearchedPages`
  // (`number[]`) by `incomplete.unsearchedPageRanges` (`[first, last]` spans).
  // One 1.5.0 body can therefore express the same fact two ways, which is
  // exactly what the paragraph below says a version must not allow. It is
  // tolerated here because the two are told apart by KEY, not by value, so no
  // reader can mistake one for the other; because `appliedLexiconsFromAssetBody`
  // migrates the old key forward on read, so the ambiguity drains out of a
  // document the first time it is opened; and because the alternative — a 1.6.0
  // that the non-lexicon fallback would also have to track — buys nothing a
  // reader of `details` can act on. A key added to `incomplete` after this
  // ships does need its own bump, by the rule above.
  //
  // The watermark versions are ADDITIVE-key bumps, which is worth saying because
  // the temptation was to skip them: nothing breaks today either way. But this
  // field exists so a reader can answer "what is in this asset?" from the
  // `details` metadata WITHOUT fetching the body out of object storage, and a
  // version that means two different things depending on when it was written
  // cannot answer that. Safe to bump because `isAssetFormatSupported` is a floor
  // check with no ceiling — an older build still loads a newer asset and simply
  // ignores the key it does not know.
  //
  // WE DO NOT ALWAYS WRITE THE LATEST, and the comment here used to say we did
  // (review-gate #3 N1). 1.5.0 is stamped only when the body actually carries
  // lexicon data; a document with none is written as 1.3.0, so BR-52's
  // "documents without lexicons keep the old format" holds literally and an
  // operator can find the affected documents by version. Loading is unchanged:
  // >= MIN_SUPPORTED_ASSET_FORMAT_VERSION (see isAssetFormatSupported).
  //
  // The conditional stamp is a TWO-VALUED special case, not a general rule. It
  // works because the non-lexicon fallback tracks the highest non-lexicon
  // version — watermarks moved it 1.1.0 -> 1.3.0, and anything orthogonal that
  // lands next has to move it again. When a third independent feature wants the
  // same treatment, replace the ternary in `redactionsToAssetBody` with a
  // `max()` over an ordered feature-predicate table rather than nesting another
  // condition.
  //
  // Benign today, and checked: the load path gates only on
  // `isAssetFormatSupported`, `stripLegacyGlyphPad` fires only on '0.0.3', and
  // both `readAssetVersion` and the 5-slot rotation key off `details.version`
  // (numeric), not this field. So a document that gains and then loses lexicon
  // data — undoing an apply reverts `appliedLexicons` — writes a LOWER format
  // than its predecessor with no functional consequence, and the rollback
  // detector over-reports rather than under-reports.
  assetFormatVersion:
    | '1.5.0'
    | '1.4.0'
    | '1.3.0'
    | '1.2.0'
    | '1.1.0'
    | '1.0.0'
    | '0.0.3';
  tdoId: TDOId;
  /**
   * Bates stamping for this document (VE-24447). Absent when the user has not
   * set one up, so documents without it round-trip byte-identical.
   */
  batesStamp?: BatesStampSettings;
  /**
   * Keyed by 1-based page number (as a string), or the literal `"all"`
   * for redactions that apply to every page (VE-20838 "Stick to all
   * pages"). The `"all"` bucket carries the same `manual`/`keyword`
   * entries shape — the burn-in worker expands each entry to every
   * page at export time.
   */
  redactions: {
    [pageOrAll: string]: {
      // `manual` covers both rectangles and custom polygons (the latter
      // carry `isCustomPolygon: true` + their vertices in `points`).
      manual?: { [redactionId: string]: PersistedRedactionEntry };
      keyword?: { [redactionId: string]: PersistedRedactionEntry };
      /**
       * Text-select redactions — created via Redact Selection / Redact
       * All Matches by highlighting text in the PDF. Stored separately
       * from `manual` so the "anchored to glyphs" behavior (no drag, no
       * stick-to-all-pages) survives save+reload.
       */
      'text-select'?: { [redactionId: string]: PersistedRedactionEntry };
      /**
       * DEPRECATED wire name for what the UI now calls "Delete Page". Read for
       * legacy assets; still written alongside `pageHidden` (see below).
       */
      pageRemoved?: true;
      /**
       * DEPRECATED wire name for what the UI now calls "Remove Page". Read for
       * legacy assets; still written alongside `pageContentRemoved`.
       */
      pageFullyRedacted?: true;
      /**
       * "Remove Page": the page stays in the burned-in PDF but its content is
       * wiped and replaced with the FOIA-style slip-sheet (white page + "This
       * page has been intentionally removed" notice). Page count is preserved.
       *
       * Deliberately NOT named `pageRemoved` — that name is already taken by
       * legacy assets where it meant the opposite (drop the page entirely, now
       * "Delete Page"). Reusing it would silently invert the meaning of every
       * asset saved before this rename.
       */
      pageContentRemoved?: true;
      /**
       * "Delete Page": the page is dropped from the output entirely by the
       * burn-in worker, so the PDF has fewer pages than the source. Sibling
       * `manual`/`keyword` entries on the same page are kept in the asset for
       * round-trip fidelity in case the user un-hides the page later.
       *
       * Gated behind the `enablePageDeletion` feature flag (default ON).
       */
      pageHidden?: true;
      /**
       * PII matches the user added on this page (VE-21915 follow-up). One
       * entry per match, keyed within the array by its vendor `id`. Presence
       * means the match is in the Redactions list; `redacted` is whether it's
       * burned in. The PII-selection mechanism for the editor.
       */
      PII?: PersistedPiiMatch[];
      /**
       * Lexicon matches on this page (VE-24948 / U5). One entry per match,
       * carrying its glyph geometry — see `PersistedLexiconMatch` and the FD-1
       * note on `PersistedRedactionState.lexiconDetections` for why the
       * geometry is stored rather than recomputed.
       */
      LEXICON?: PersistedLexiconMatch[];
    };
  };
  /**
   * Lexicon detection cards (VE-24948 / U5) — one per (lexicon, entry) pair.
   *
   * At the asset ROOT rather than inside a page bucket because a card spans
   * pages: its matches live in the per-page `LEXICON` arrays and reference it
   * by `detectionId`. Storing the snapshot once per card rather than once per
   * match also keeps the payload proportional to entries, not to hits.
   *
   * Optional, and `assetFormatVersion` is deliberately NOT bumped: a reader
   * that predates U5 ignores unknown root keys, which is the direction BR-53
   * requires (a save written with U5's fields must load in a build without it).
   */
  lexiconDetections?: PersistedLexiconDetection[];
  /** Lexicons applied to this document (`U5-FR14` at upload, `U5-FR18` in the editor). */
  appliedLexicons?: AppliedLexiconRecord[];
  /**
   * Watermarks applied to the document (VE-24693). Stored at the top level (not
   * per page) since a watermark burns onto every surviving page. Each entry is a
   * snapshot copied from the library, so it round-trips independently of the
   * library entry (AC#3). Optional so older assets / watermark-free docs stay
   * byte-identical for readers that don't know about it.
   */
  watermarks?: AppliedWatermark[];
  /**
   * Present (and `true`) only when the watermark switch is off — see
   * `SessionState.watermarksOff`. Absent means on, so every asset written before
   * this existed reads as it always did.
   */
  watermarksOff?: true;
  /**
   * Bates settings kept while numbering is switched off — see
   * `SessionState.batesStampOff`. Never present alongside `batesStamp`, and
   * absent otherwise, so a reader that does not know it sees exactly what it
   * saw before: an unnumbered document. No format bump, like `watermarksOff`:
   * an optional key an older build ignores, still inside 1.4.0.
   */
  batesStampOff?: BatesStampSettings;
}

/**
 * One persisted lexicon match (VE-24948 / U5).
 *
 * `id` is the deterministic `matchId` (see `lexiconMatchId`) — the key the
 * reviewer's triage state hangs off, which is why it must not be random.
 *
 * Geometry is persisted as `points` outlines, the same canonical form
 * `PersistedRedactionEntry` uses, so the same validators and `pointsToBox`
 * apply on load. A match that WRAPS ACROSS LINES has one outline per row: a
 * single union box would cover the rectangle between the rows and redact
 * unrelated text (see `glyphSpanGeometry`).
 */
export interface PersistedLexiconMatch {
  /** Deterministic match id. */
  id: string;
  /** The card this match belongs to — `PersistedLexiconDetection.id`. */
  detectionId: string;
  /** One outline per row of the match, at `DOCUMENT_REDACTION_COORD_PRECISION`. */
  rects: PersistedPoint[][];
  /** Whether this specific match is burned in. */
  redacted: boolean;
  /**
   * Set when `redacted` was decided by AUTO REDACT rather than by a person.
   *
   * A reviewer's redaction and an automatic one are otherwise identical in the
   * saved asset, so nothing could answer "did anybody look at this before it
   * was burned in?" — and it cannot be reconstructed later, because a reviewer
   * opening the document afterwards leaves no trace of what was already
   * redacted when they arrived. Free to record at the moment of creation,
   * impossible afterwards.
   *
   * NOT cleared when a reviewer un-redacts and re-redacts the same match: the
   * flag describes how the match FIRST became redacted, which is the question
   * an audit is asking. Absent means a person did it, which is what every
   * record written before this field describes.
   */
  autoRedacted?: boolean;
  /**
   * Set when the reviewer dismissed this match. Persisted because dismissals
   * MUST survive re-application (BR-41) — resurrecting one is the same defect
   * class as resetting a reviewer's decisions, which US-U5-6 forbids.
   */
  deleted?: boolean;
}

/**
 * One persisted lexicon detection card (VE-24948 / U5).
 *
 * Carries the entry SNAPSHOT, never a lexicon reference: that is the mechanism
 * behind `U5-FR2` / BR-44, and it is what keeps the source indicator meaningful
 * after the lexicon is edited or deleted.
 *
 * `checked` / `checkedPages` / `redactedMatchIds` / `deletedMatchIds` are NOT
 * stored — they are derived from the per-match `redacted` / `deleted` flags on
 * load, exactly as the PII path derives them via `applyPiiMatches`. Storing
 * both would let the two disagree.
 */
export interface PersistedLexiconDetection {
  id: string;
  /**
   * The card's display label.
   *
   * Stored rather than re-derived from `snapshot.term` so that a future change
   * to how cards are labelled cannot silently retitle every already-saved
   * document on reload.
   */
  label: string;
  snapshot: LexiconEntrySnapshot;
  /** Whether the card has been promoted into the Redactions list. */
  addedToRedactions?: boolean;
  redactionCode?: RedactionCodeAssignment;
}

/**
 * What the watermark panel is currently SHOWING, as opposed to what the
 * document has (VE-24693, brought into line with Bates in review).
 *
 * The same contract as `BatesStampPreview`, over a LIST: while the panel is
 * open, `ADD_WATERMARK` / `UPDATE_WATERMARK` / `REMOVE_WATERMARK` land here
 * instead of on the session, and Apply is what moves them across. Routing it in
 * the reducer rather than at each dispatch site is what lets the canvas overlay
 * stay exactly as it was — dragging a mark writes the same action it always
 * did, and the preview catches it.
 *
 * `marks` holds every applied watermark, not a delta: the panel adds, removes
 * and edits, so a patch-list would have to be replayed to be read, and the page
 * needs to read it on every render.
 */
export interface WatermarkPreview {
  readonly marks: AppliedWatermark[];
  /** Whether the preview draws at all — the panel's on/off switch. */
  readonly enabled: boolean;
}

/**
 * What the Bates panel is currently SHOWING, as opposed to what the document
 * has (VE-24447).
 *
 * The panel is a preview session: while it is open the page draws these
 * settings, and the document's own `session.batesStamp` is not touched until
 * Done. That is the whole point — opening the panel, or fiddling with a
 * placement, must not stamp an official record, push undo entries, or trigger
 * an autosave. Cancelling clears this and the document is exactly as it was.
 *
 * `settings` is always present, even while `enabled` is false: the toggle means
 * "don't number the document", not "throw away what I typed", so the panel's
 * fields keep their values and turning it back on costs nothing. `enabled`
 * false is what Done commits as a REMOVAL.
 */
export interface BatesStampPreview {
  readonly settings: BatesStampSettings;
  /** Whether the preview draws at all — the panel's on/off switch. */
  readonly enabled: boolean;
}

/**
 * Top-level slice shape.
 */
export interface DocumentsStore {
  /**
   * The document on screen, or null when none is open.
   *
   * VE-24947 kept this as a single slot rather than folding it into
   * `suspendedSessions` as `sessions[activeTdoId]`. The active session is
   * written in exactly three places but READ — and mutated through — in 373,
   * behind 81 `if (state.session)` narrowing guards. Making it a map lookup
   * would have rewritten every one of those to re-acquire and re-narrow a
   * draft, for no behaviour anyone asked for. The active document genuinely is
   * special: it is the one being rendered.
   */
  session: SessionState | null;

  /**
   * Documents the reviewer switched away from that still hold unsaved work,
   * keyed by TDO (VE-24947 / U2-FR2).
   *
   * A reviewer working through a request should not have to make a save
   * decision at every switch, so switching SUSPENDS the outgoing document here
   * instead of discarding it, and switching back restores it — unsaved
   * redactions, detections, undo history and all.
   *
   * Only DIRTY sessions are suspended. A clean document loses nothing by being
   * re-opened from the server, and keeping every visited file resident is the
   * unbounded-memory failure NFR1 is about. `suspendIfWorthKeeping` is the one place
   * that rule lives.
   *
   * These are NOT rendered, so nothing outside the lifecycle reducers should
   * read them directly — use `selectUnsavedDocuments` / `selectOpenDocumentIds`,
   * which account for the active session too.
   */
  suspendedSessions: Record<TDOId, SessionState>;

  /**
   * The document a switch is on its way to, or null when none is in flight
   * (VE-24947).
   *
   * While this is set, `session` is still the OUTGOING document. That is
   * deliberate: it keeps the editor's chrome — top bar, bottom bar, Documents
   * panel — mounted and rendering through the switch, so only the canvas, page
   * rail and detections panel show a loading state. Nulling the session instead
   * put the whole page behind a full-screen spinner and remounted every panel.
   *
   * The swap is atomic, in OPEN_DOCUMENT_SUCCESS: the outgoing document is
   * suspended and the incoming one installed in the same action, so there is no
   * moment where neither is the session. `documentName` is carried so the
   * header can name where it is going while it gets there — the Documents panel
   * row that was clicked already knows it, and the server has not answered yet.
   */
  /**
   * A RELOAD FOR OCR is under way and the lexicon re-run it causes has not
   * reported yet.
   *
   * Store-level rather than on the session, because the session is exactly what
   * the reload replaces — a flag inside it would be reset by the very event it
   * is meant to span.
   *
   * It exists because the honest window is wider than the run. The re-run only
   * starts once the new session's pre-pass has extracted text, which on a long
   * document is seconds after the reviewer pressed Reload; a notice keyed on the
   * run alone appeared late, over a panel whose numbers had already been stale
   * for the whole gap. Set when the reload is asked for, cleared when the run
   * reports — or at `OPEN_DOCUMENT_SUCCESS` when it turns out there is nothing
   * to re-run, so it cannot stick on a reload that changed no file.
   */
  ocrReloadInProgress: boolean;

  pendingDocument: {
    tdoId: TDOId;
    documentName: string;
    /**
     * The tab to land on. Carried because switching from the Redacted File tab
     * means "show me THAT file's redacted output" — landing on the editor
     * (which is where a fresh session starts) would answer a question the
     * reviewer did not ask.
     */
    activeTab: ActiveTab;
    /**
     * Does the INCOMING document already have a redacted output?
     *
     * Known before the fetch — the Documents panel reads it off the
     * case-contents query — and needed during the wait for two things the
     * outgoing session would answer wrongly: whether the page rail will be
     * there (it is hidden on the Redacted Files tab with no output, and the
     * placeholder must reserve the same width the loaded view will), and
     * whether there is a redacted filename worth naming at all.
     */
    hasRedactedOutput: boolean;
  } | null;
  /**
   * Lexicons picked in the upload dialog (VE-24948 / U5, `U5-FR14`), waiting
   * for the upload batch to start. Slice-level, not session — there is no
   * session at upload time. When the batch's files finish uploading,
   * `STAGE_UPLOAD_LEXICON_BATCH` snapshots this into `uploadLexiconBatch`
   * (and clears it); BR-49 forbids any evaluation here.
   */
  uploadLexiconSelection: string[];
  /**
   * Lexicons whose matches are redacted on arrival rather than offered for
   * review — a per-lexicon PREFERENCE, not a subset of the selection.
   *
   * It is deliberately independent: Auto Redact All turns every row on without
   * selecting anything, and a row switched on, unticked and ticked again comes
   * back on. Only `STAGE_UPLOAD_LEXICON_BATCH` intersects the two, because
   * that is the point where the pair stops being a preference and becomes what
   * gets recorded against a document.
   */
  uploadLexiconAutoRedact: string[];
  /**
   * The selection snapshotted against ONE upload batch's files, keyed by
   * their upload URLs (VE-24948 / U5, `U5-FR14`).
   *
   * The batch exists so that EVERY PDF of a multi-file upload gets the
   * selection recorded, while a TDO created outside the batch — a later
   * upload, or the URL-ingest path, which never shows the selector — cannot
   * inherit it. Consuming the plain selection per `CREATE_TDO_SUCCESS` and
   * clearing on first use recorded the selection against the FIRST file only
   * (review-gate finding 2). Each recording consumes its own `fileUrl`; the
   * batch clears when the last is consumed, and the next staging replaces
   * whatever is left (a failed file's URL is never consumed).
   */
  uploadLexiconBatch: {
    lexiconIds: string[];
    /** Subset of `lexiconIds`; see `uploadLexiconAutoRedact`. */
    autoRedactLexiconIds: string[];
    fileUrls: string[];
  } | null;
  /**
   * Set when the last OPEN_DOCUMENT attempt failed (so the route can render
   * an error UI instead of a perpetual loading screen). Cleared whenever a
   * new OPEN_DOCUMENT is dispatched.
   */
  loadError: string | null;

  /**
   * Document-comment state (VE-21982). Lives at the slice top level (not
   * inside `session`) to mirror the mediaDetails pattern and so undo/redo
   * snapshots — which only capture session fields — never touch comment
   * data. Cleared on OPEN_DOCUMENT (so a stale list never flashes in for
   * the next document) and CLOSE_DOCUMENT.
   */
  documentComments: { [commentId: string]: DocumentComment };
  /** In-progress text keyed by comment id; never persisted or sent to the API. */
  documentCommentDrafts: { [commentId: string]: string };
  isFetchingDocumentComments: boolean;
  selectedDocumentComments: string[];
  /**
   * commentId currently being edited in the CommentsPanel, or `null` when
   * no row is in edit mode. Lifted into redux so that callers outside the
   * panel — e.g. the document's right-click "Add Comment" menu — can drop a
   * freshly-created comment straight into edit mode without needing a
   * direct ref to the panel.
   */
  editingDocumentCommentId: string | null;
  /**
   * commentId the user just asked to "focus" — set by clicking a floating
   * comment icon on the page. The Comment row watches this and triggers
   * scrollIntoView when it matches; it's cleared by the row itself after
   * scrolling so the same icon can be re-clicked to scroll again.
   */
  focusedDocumentCommentId: string | null;

  /**
   * The Bates panel's live preview, or null when the panel is closed
   * (VE-24447). Top level rather than inside `session`, for the same reason as
   * the comment state above and one more: undo/redo snapshots capture session
   * fields, and a preview must never become an undo step — it is not a change
   * to the document. It is also never persisted, so the redaction-data asset
   * and the export both continue to read `session.batesStamp` alone.
   */
  batesPreview: BatesStampPreview | null;

  /**
   * The watermark panel's live preview, or null when the panel is closed
   * (VE-24693). Top level and never persisted, for the same reasons as
   * `batesPreview` above.
   */
  watermarkPreview: WatermarkPreview | null;

  /**
   * "Send to" integration state (VE-23442). Lives at the slice top level (not
   * inside `session`, like `documentComments`) so undo/redo snapshots — which
   * only capture session fields — never touch in-flight send state, and so the
   * generic integration's fetched `config` survives a session re-open. Reset
   * to defaults on OPEN_DOCUMENT.
   */
  integrations: DocumentIntegrationsState;

  /**
   * VE-24947 (U2-FR3) — is the Documents panel open in the editor?
   *
   * Top level, NOT inside `session`, and deliberately so: switching documents
   * is a route change that opens a new session, and a reviewer working through
   * a request should not have the panel they are navigating with close itself
   * on every file they pick. Session state would do exactly that.
   *
   * Also why it is not `useState` in the view, which unmounts on the switch.
   *
   * CLOSE_DOCUMENT deliberately does NOT reset it. That action means the
   * reviewer LEFT the editor — it also wipes `suspendedSessions`, which is
   * precisely why a switch must never dispatch it — and the panel's open state
   * is a sticky chrome preference for the tab rather than something owned by
   * one editing session. It survives to the next document the reviewer
   * opens.
   */
  documentsPanelOpen: boolean;

  /**
   * VE-29142 — should the Documents panel ARRIVE at full width, rather than
   * resting at the peek strip until the pointer visits it?
   *
   * Separate from `documentsPanelOpen` because opening it and restoring it are
   * different intents. Opening it is a request to READ it, so it arrives
   * expanded and shrinks once the reviewer has had their look. RESTORING it
   * from the stored preference is not a request for anything — it is where the
   * panel lives for this request — so it should arrive as the strip, which is
   * what the reviewer actually left behind.
   *
   * Reading the two as one meant a stored "open" reopened the editor with the
   * panel covering the page rail, and the only way to dismiss it was to hover
   * it and move away: a grace period spent on an intent nobody had.
   *
   * Pinned is unrelated and wins on its own — a pinned panel stays expanded
   * whatever this says.
   */
  documentsPanelExpanded: boolean;

  /**
   * VE-24947 — is the Documents panel pinned?
   *
   * ONE pin, doing what "pinned" means for a side panel everywhere else: it
   * stops floating and starts occupying. Two consequences, both visible the
   * moment it is pressed — the panel no longer falls back to its strip when
   * the pointer leaves, and it PUSHES the page rail right rather than
   * expanding over it, so the thumbnails stay visible beside it.
   *
   * That pairing is why the Pages panel has no pin of its own: "keep documents
   * up" and "do not cover my thumbnails" turned out to be one intent. A
   * reviewer who wants only the document list pins it and closes the rail.
   *
   * Slice-level for the same reason `documentsPanelOpen` is: chrome
   * preferences should survive a document switch, which opens a new session.
   */
  documentsPanelPinned: boolean;

  /**
   * Does the tool rail show its buttons' names, at the cost of ~130px of
   * canvas?
   *
   * Slice-level rather than component-local because it is PERSISTED — remembered
   * per window band, hydrated at open and written on every change (see
   * `readToolPanelExpanded` and the panel-preferences saga). Local state could
   * not survive the reload, and putting the read in the component would make
   * the rail fetch its own preference on every mount.
   *
   * Note the grain: this one is keyed by window BAND where its three neighbours
   * above are keyed by request. That is deliberate — see the storage module.
   */
  toolPanelExpanded: boolean;

  /**
   * VE-24947 (U2-FR5) — the reviewer's chosen order for a request's documents,
   * or null while they have not reordered anything.
   *
   * SESSION-ONLY and deliberately so for now: it lives here, not on the server,
   * so it survives switching between documents in the request but not a reload.
   * Persisting it needs a decision about where a per-request ordering belongs
   * and who else reads it — the combined-PDF export certainly will.
   *
   * Scoped to the request it was made in (`folderId`). An order from one
   * request means nothing in another, and the panel is repopulated per request,
   * so applying a stale one would silently shuffle a different set of files.
   *
   * Holds ids, not indices: the underlying list is refetched and can gain or
   * lose files while the order is held, so consumers append what they do not
   * recognise and ignore what has gone.
   */
  documentOrder: { folderId: string; order: TDOId[] } | null;

  /**
   * VE-24947 — is an explicit Save All pass running?
   *
   * Distinct from "some session is saving", which is also true of a routine
   * autosave tick. The leave guard needs to tell those apart: `hasUnsavedWork`
   * excludes `saving` on purpose, so that a 5-minute autosave landing as the
   * reviewer closes does not accuse them of unsaved work. But a save they
   * PRESSED is work in flight that leaving would abandon, and that is worth
   * stopping for.
   */
  saveAllInProgress: boolean;

  /**
   * VE-24947 — the document whose burn-in just finished while the reviewer had
   * UNSAVED edits on it, or null.
   *
   * A finished burn-in normally flips straight to the Redacted Files tab. That
   * is right when the tab shows what the reviewer was waiting for — and wrong
   * when they kept working while it ran, because the file it renders predates
   * those edits. Yanking them to a stale preview interrupts editing to show
   * something already out of date. So when there is unsaved work the tab holds
   * and this raises a notice instead, leaving the move over to them.
   *
   * Set only for the document ON SCREEN. A burn-in finishing on a suspended
   * document has nothing to interrupt.
   */
  redactionCompleteNotice: TDOId | null;

  /**
   * VE-29142 — documents THIS editor started a burn-in on, by name.
   *
   * In the slice rather than a component ref, which is where it started. A
   * burn-in SAVES before it burns, so its document is clean for most of its
   * own run — and only documents worth keeping are suspended, so a clean one
   * switched away from leaves redux entirely. Nothing in the store could then
   * answer "is a burn-in this editor started still running", which left the
   * back button and the tab-close warning silent about a burn-in the in-app
   * close DID warn about. The three exits cannot share one predicate while one
   * of its inputs lives in a component.
   *
   * Scoped to what this editor launched, so a burn-in started from the gallery
   * or the media editor — which leaving this page does not disturb — is not
   * counted. Cleared by CLOSE_DOCUMENT alongside `suspendedSessions`: both
   * describe one editing session.
   */
  burnInsStartedHere: Record<string, string>;

  /**
   * VE-24947 — is the Pages panel hidden?
   *
   * Slice-level, like its siblings above, because it used to be `useState` in
   * DocumentEditorView — which is keyed on `session.tdoId` and so REMOUNTS on
   * every document switch. Hiding the rail and moving to the next document
   * brought it straight back, while the Documents panel's own state survived,
   * so two adjacent panels behaved differently for no reason a reviewer could
   * guess.
   */
  pagesPanelCollapsed: boolean;
}

/**
 * Document-comment record. Mirrors `MediaComment` from
 * `@common-modules/mediaDetails` but with no `mediaTimestamp` and with an
 * optional document anchor — `{ page, x, y, width, height }` — so a comment
 * can be either document-level (un-anchored) or pinned to a specific region
 * on a specific page. All anchor fields are optional in the schema; until
 * the anchored-comments UI ships (VE-21982 follow-up) the panel sends them
 * as `undefined` placeholders so the service signature is already shaped.
 */
export interface DocumentComment {
  readonly tdoId: TDOId;
  readonly done: boolean;
  readonly read: boolean;
  readonly comment: string;
  readonly deleted: boolean;
  readonly archived: boolean;
  readonly commentId: string;
  readonly createdBy: string;
  readonly searchName: string;
  readonly createdDateTime: string;
  readonly modifiedDateTime: string;
  /** Mirrors `MediaComment.id` — set to commentId by the BFF on round-trip. */
  readonly id: string;
  /**
   * Marker used by the panel to distinguish a comment that's been freshly
   * created locally (and not yet persisted) from a saved one — same as the
   * media-comments flow. Lets cancel-edit drop the local row cleanly.
   */
  readonly isCancelledNewComment?: boolean;
  /** 1-based page anchor. Undefined for document-level comments. */
  readonly page?: number;
  /** Anchor rect, PDF user-space units, top-left origin. */
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
}

// ---------------------------------------------------------------------------
// OCR manifest asset
//
// The `redact-document-ocr-manifest` asset the Redact Document OCR engine
// publishes beside the searchable PDF. Once OCR text is inside a PDF nothing
// downstream can tell it from text an author typed — which is fine for finding
// words and useless for telling the truth about them, and the ticket asks for
// that truth in four places (FR5, FR6, FR7, FR10). The words live in the
// document; the facts about the words live here.
//
// These types mirror the engine's `searchable_pdf/manifest.py` exactly. Keep
// them in step with it: a field renamed on one side and not the other fails
// silently, because a manifest that parses to all-undefined reads as "no OCR
// information" rather than as an error. `assetFormatVersion` is carried for
// diagnostics only — nothing gates on it.
// ---------------------------------------------------------------------------

/**
 * How a page's text got there.
 *   - `native`  — the author's own text layer; OCR did not touch the page
 *   - `ocr`     — OCR read it and wrote words in
 *   - `empty`   — OCR read it and found nothing (a blank scan)
 *   - `failed`  — OCR tried and could not; must NEVER read to the user as
 *                 "searched"
 *   - `skipped` — OCR deliberately did not attempt the page
 */
export type OcrPageSource = 'native' | 'ocr' | 'empty' | 'failed' | 'skipped';

/**
 * Why a region is worth showing the reviewer.
 *   - `unread`    — no text was written for this writing, either because it
 *                   could not be read at all or because it read below the
 *                   confidence threshold and was withheld
 *   - `uncertain` — read below the threshold and written in anyway: searchable
 *                   but doubtful. Gated behind the engine's
 *                   `write_low_confidence` option, which production does not
 *                   set, so no manifest carries one today
 */
export type OcrRegionKind = 'unread' | 'uncertain';

/**
 * An area of a page the reviewer should not assume was searched.
 *
 * These types are deliberately NOT `readonly`: a manifest is held in the immer
 * -managed session slice, and a readonly array is not assignable to immer's
 * WritableDraft. Treat them as immutable by convention, as with the rest of the
 * session's data.
 */
export interface OcrRegion {
  kind: OcrRegionKind;
  /**
   * `[x0, y0, x1, y1]` in PDF points with a TOP-LEFT origin — the same frame
   * the editor stores redactions in, so these need no conversion before being
   * drawn.
   */
  box: [number, number, number, number];
  confidence?: number;
}

export interface OcrPageRecord {
  /** 1-based, matching the editor's page numbering. */
  page: number;
  source: OcrPageSource;
  meanConfidence?: number;
  /**
   * Text segments written — the OCR model detects LINES, not words, so this is
   * usually a line, sometimes a single form-field value. Present only when
   * `source === 'ocr'`.
   */
  segmentsWritten?: number;
  regions?: OcrRegion[];
  /** Set only when `source === 'failed'`. */
  reason?: string;
  /** The page's `/Rotate` value; non-zero pages warrant extra scrutiny. */
  rotation?: number;
}

export interface OcrManifest {
  assetFormatVersion: string;
  /**
   * Must equal the PDF's own page count. The engine writes it precisely so a
   * manifest that has drifted from the file it describes can be caught and
   * ignored rather than mis-reported per page.
   */
  pageCount: number;
  engine: string;
  engineVersion: string;
  confidenceThreshold: number;
  pages: OcrPageRecord[];
}

/**
 * Lifecycle of the OCR engine for this session.
 *
 * Nothing here gates the open — a document opens against whichever PDF is on the
 * TDO, and these states drive what the editor SAYS about search, not whether the
 * file is available. Surfaces must not read them directly: `ocrSearchOutlook`
 * (selectors.ts) collapses them into the cases the copy actually distinguishes,
 * so the rail and the page notice cannot disagree.
 *
 *   - `checking`          — on open, still asking the server what happened.
 *     Also covers a TDO whose job is running but has not created its OCR task
 *     yet, which is the ordinary case seconds after upload. Says nothing.
 *   - `not-run`           — no OCR task on this TDO and no job that could still
 *     produce one. Every document ingested before the OCR engine existed
 *     lands here, and it is indistinguishable from (and treated the same as)
 *     OCR never being configured: no OCR messaging.
 *   - `indeterminate`     — the status could not be established: the check
 *     errored, or a job outlasted the wait for its task. Distinct from `not-run`
 *     because that one licenses "no searchable text — manual review
 *     recommended", and a failed lookup is no evidence for it.
 *   - `processing`        — an OCR task is in flight. The pages on screen are
 *     the pre-OCR ones and search does not reach them yet; both are said.
 *   - `refresh-available` — OCR finished and republished mid-session. The
 *     session keeps the file it opened; the reviewer is offered a reload (the
 *     rail states it, the button is on `OcrReloadBanner` above the canvas).
 *     Per-page notices go quiet — the banner is the single voice for it.
 *   - `done`              — OCR finished and the manifest was loaded.
 *   - `failed`            — the OCR task reached a terminal non-complete state.
 *     Search is degraded, and the reviewer is told.
 */
export type OcrEngineState =
  | 'checking'
  | 'not-run'
  | 'indeterminate'
  | 'processing'
  | 'refresh-available'
  | 'done'
  | 'failed'
  /** Unfinished 12h after the task was created; see `TASK_STALL_MS`. */
  | 'timed-out';
